export const BASE_PROMPT = `PHOTO EDIT — Ultra-realistic, 8k resolution, award-winning photography. EDIT THE REFERENCE PHOTO. Only change costume, setting, and style. Keep every face and person identical.

1. CRITICAL: EXACT FACE COUNT. Count the faces in the reference photo and output EXACTLY the same number of people. If there are 4 people, output 4 people. DO NOT add, remove, or replace anyone.
2. CRITICAL: PRESERVE EVERY PERSON'S SKIN TONE, ETHNICITY, AND RACIAL IDENTITY EXACTLY as seen in the reference photo. NEVER alter race, complexion, or ethnic appearance.
3. CRITICAL: DO NOT CHANGE ANY FACIAL FEATURES. The eyes, nose, mouth, face shape, and identity of ALL people must be pixel-perfect matches to the reference photo. Every face must be instantly recognizable as the same person.
4. CRITICAL: 4K/8K SHARP FACES. ZERO blurriness, distortion, or AI smoothing on any face. Hyper-realistic skin texture, pores, and micro-details in razor-sharp focus.
5. CRITICAL: ALL faces must be FULLY VISIBLE and UNCOVERED. NO masks, NO helmets over the face, NO veils. Only change costume — never cover the face.
6. Match the character role, costume, and pose to each person's gender and body type from the reference photo.
7. ONLY CHANGE: costume/clothing, background/setting, lighting mood, and artistic style. Keep EVERYTHING about the people's faces and identities unchanged.
8. Professional Rembrandt lighting, 85mm lens, f/1.8 aperture, cinematic 8k.`;

const REALISM_SUFFIX = 'highly detailed, photorealistic, 8k resolution, cinematic lighting, sharp focus, f/1.8, 85mm lens';

const EXPERT_PROMPTS_BY_ID = {
  // Classic Superheroes
  superman: 'Iconic superhero in textured royal blue suit with red cape flowing naturally, red boots, athletic muscular build, standing confidently on skyscraper rooftop at golden hour, volumetric sunset lighting, city skyline in background, photojournalistic realism, face clearly visible and well-lit, heroic confident expression, wind in hair',
  
  wonderwoman: 'Amazonian warrior princess in bronze and red armor with golden accents, silver bracelets, athletic feminine build, ancient Greek temple ruins background, warm Mediterranean daylight, dramatic clouds, documentary realism, long flowing dark hair, face fully visible, strong noble expression',
  
  batman: 'Dark tactical hero in matte black tactical armor with utility belt, armored chest plate, rain-soaked Gotham cityscape at night, dramatic rim lighting from neon signs, moody atmosphere, gritty realism, NO COWL OR MASK, face fully visible and expressive, determined intense look, wet hair',
  
  ironman: 'High-tech armored hero, RED and GOLD metallic suit with arc reactor glowing blue, advanced Stark laboratory setting, holographic displays, crisp reflections on polished metal surfaces, HELMET COMPLETELY OFF and held under arm, face fully visible, confident genius expression, workshop atmosphere',
  
  spiderman: 'Agile hero in red and blue tactical suit with web pattern, athletic build, New York City rooftop, skyscrapers background, urban sunset, sharp focus photography, NO MASK, face fully visible, youthful energetic expression, dynamic pose',

  // ============================================
  // MEDICAL PROFESSIONALS
  // ============================================
  
  doctor_male: 'Professional male physician in crisp white lab coat over blue scrubs, stethoscope around neck, modern hospital setting with medical equipment, clean clinical LED lighting, medical charts and monitors visible in background, face clearly visible, professional compassionate demeanor, no surgical mask, confident posture',
  
  doctor_female: 'Professional female physician in crisp white lab coat over blue scrubs, stethoscope around neck, modern hospital setting with medical equipment, clean clinical LED lighting, medical charts and monitors visible in background, face clearly visible, professional compassionate demeanor, no surgical mask, confident posture',
  
  surgeon_male: 'Experienced surgeon in blue surgical scrubs, surgical cap on head, NO surgical mask (mask pulled down around neck), modern operating room background, bright surgical lighting, face fully visible, focused professional expression, scrubbed and ready',
  
  surgeon_female: 'Experienced surgeon in blue surgical scrubs, surgical cap on head, NO surgical mask (mask pulled down around neck), modern operating room background, bright surgical lighting, face fully visible, focused professional expression, scrubbed and ready',

  nurse_male: 'Professional male nurse in colored scrubs, medical badge, hospital corridor background, fluorescent lighting, caring expression, face fully visible, approachable demeanor',
  
  nurse_female: 'Professional female nurse in colored scrubs, medical badge, hospital corridor background, fluorescent lighting, caring expression, face fully visible, approachable demeanor',

  // ============================================
  // CULINARY PROFESSIONALS
  // ============================================
  
  chef_male: 'Master male chef in pristine white chef jacket and apron, tall chef hat (toque), professional kitchen with stainless steel equipment and copper pots, ambient cooking lighting, steam and atmosphere, herbs and ingredients visible, face fully visible, focused culinary expression, hands in professional pose',
  
  chef_female: 'Master female chef in pristine white chef jacket and apron, tall chef hat (toque), professional kitchen with stainless steel equipment and copper pots, ambient cooking lighting, steam and atmosphere, herbs and ingredients visible, face fully visible, focused culinary expression, hands in professional pose',
  
  pastry_chef_male: 'Expert pastry chef in white uniform, professional patisserie kitchen, marble counters, baking equipment, soft natural light, face visible, artistic concentration',
  
  pastry_chef_female: 'Expert pastry chef in white uniform, professional patisserie kitchen, marble counters, baking equipment, soft natural light, face visible, artistic concentration',

  // ============================================
  // AVIATION & AEROSPACE
  // ============================================
  
  pilot_male: 'Commercial airline captain in formal white shirt with four gold epaulettes on shoulders, navy tie, modern cockpit background with illuminated instruments and controls, professional aviation portrait lighting, captain hat held at side or removed, face fully visible, confident professional look, clear sky visible through windscreen',
  
  pilot_female: 'Commercial airline captain in formal white shirt with four gold epaulettes on shoulders, navy tie, modern cockpit background with illuminated instruments and controls, professional aviation portrait lighting, captain hat held at side or removed, face fully visible, confident professional look, clear sky visible through windscreen',
  
  fighter_pilot: 'Elite fighter pilot in olive flight suit with patches, G-suit harness, standing on runway tarmac, F-16 or F-35 jet in background, natural golden hour sunlight, raw aviation photography, helmet under arm or on ground beside, oxygen mask hanging, face fully visible, determined aviator expression',
  
  fighter_pilot_female: 'Elite female fighter pilot in olive flight suit with patches, G-suit harness, standing on runway tarmac, F-16 or F-35 jet in background, natural golden hour sunlight, raw aviation photography, helmet under arm or on ground beside, oxygen mask hanging, face fully visible, determined aviator expression',
  
  astronaut: 'NASA astronaut in white EVA spacesuit with American flag patch, detailed fabric texture and technical equipment, International Space Station interior background, Earth visible through window, crisp practical LED lighting, helmet completely off and floating nearby, face fully visible, awe-inspired expression',
  
  astronaut_female: 'NASA female astronaut in white EVA spacesuit with American flag patch, detailed fabric texture and technical equipment, International Space Station interior background, Earth visible through window, crisp practical LED lighting, helmet completely off and floating nearby, face fully visible, awe-inspired expression',

  // ============================================
  // EMERGENCY SERVICES
  // ============================================
  
  firefighter_male: 'Heroic male firefighter in tan and yellow protective turnout gear with reflective stripes, helmet held at the side or on ground, face fully visible and soot-marked, NO mask covering face, fire station background with fire truck, realistic gritty textures, dramatic lighting, brave determined expression',
  
  firefighter_female: 'Heroic female firefighter in tan and yellow protective turnout gear with reflective stripes, helmet held at the side or on ground, face fully visible and soot-marked, NO mask covering face, fire station background with fire truck, realistic gritty textures, dramatic lighting, brave determined expression',
  
  paramedic_male: 'Emergency paramedic in navy blue uniform with medical patches, ambulance background, emergency lights, face visible, ready-for-action expression',
  
  paramedic_female: 'Emergency paramedic in navy blue uniform with medical patches, ambulance background, emergency lights, face visible, ready-for-action expression',

  police_officer_male: 'Police officer in navy blue uniform, badge visible, urban street background, patrol car, professional law enforcement look, face fully visible, authoritative presence',
  
  police_officer_female: 'Police officer in navy blue uniform, badge visible, urban street background, patrol car, professional law enforcement look, face fully visible, authoritative presence',

  // ============================================
  // MILITARY
  // ============================================
  
  soldier: 'Soldier in camouflage tactical uniform with gear and vest, desert terrain background with sand dunes, realistic fabric textures, harsh natural sunlight, NO helmet (helmet removed or at side), face fully visible with desert tan, disciplined military bearing',
  
  soldier_female: 'Female soldier in camouflage tactical uniform with gear and vest, desert terrain background with sand dunes, realistic fabric textures, harsh natural sunlight, NO helmet (helmet removed or at side), face fully visible with desert tan, disciplined military bearing',
  
  navy_seal: 'Elite special forces operator in tactical black gear, ocean or beach background, sunset lighting, helmet off, face visible, intense focused expression',
  
  marine: 'US Marine in dress blues uniform with medals, American flag background, formal military portrait lighting, cover (hat) removed, face visible, proud dignified expression',

  // ============================================
  // SPORTS & ATHLETICS
  // ============================================
  
  soccer: 'Professional male soccer player in team jersey and shorts, large stadium background with crowd, floodlights creating dramatic atmosphere, realistic grass texture, athletic physique, sweat and intensity, face fully visible, competitive expression, soccer ball at feet',
  
  soccer_female: 'Professional female soccer player in team jersey and shorts, large stadium background with crowd, floodlights creating dramatic atmosphere, realistic grass texture, athletic physique, sweat and intensity, face fully visible, competitive expression, soccer ball at feet',
  
  basketball: 'Professional male basketball player in team jersey, indoor court with hardwood floor, arena lights, professional sports photography, tall athletic build, face fully visible, game-ready expression, basketball in hand',
  
  basketball_female: 'Professional female basketball player in team jersey, indoor court with hardwood floor, arena lights, professional sports photography, athletic build, face fully visible, game-ready expression, basketball in hand',
  
  boxer: 'Professional boxer in boxing gloves and shorts, boxing gym background with ring and punching bags, dramatic rim lighting, realistic sweat and muscle definition, athletic stance, face fully visible, fierce determined expression',
  
  boxer_female: 'Professional female boxer in boxing gloves and sports attire, boxing gym background with ring and punching bags, dramatic rim lighting, realistic sweat and athletic build, strong stance, face fully visible, fierce determined expression',
  
  tennis_player: 'Tennis athlete in sport outfit, tennis court background, racket in hand, dynamic pose, stadium atmosphere, face visible, focused competitive look',
  
  football_player: 'American football player in team uniform with pads, helmet OFF and held at side, football field background, stadium lights, athletic build, face visible, game intensity',
  
  baseball_player: 'Baseball player in team uniform, baseball diamond background, bat or glove, stadium atmosphere, cap worn normally, face fully visible, ready stance',

  // ============================================
  // FANTASY & MAGIC
  // ============================================
  
  wizard: 'Wise male wizard in deep blue school robes with house colors, wooden wand, ancient library background with floating candles and leather-bound books, soft bokeh lighting, practical interior lighting, documentary realism, beard or clean-shaven, face fully visible, mysterious knowing expression',
  
  witch: 'Skilled female witch in dark school robes with house colors, wooden wand, ancient library background with floating candles and mystical artifacts, soft bokeh lighting, practical interior lighting, documentary realism, long flowing hair, face fully visible, intelligent enigmatic expression',
  
  sorcerer: 'Powerful sorcerer in ornate magical robes with mystical symbols, glowing staff, arcane chamber background, magical particles in air, dramatic lighting, face visible, ancient wisdom in eyes',
  
  elf_female: 'Graceful elven warrior in silver leaf armor with intricate designs, pointed ears, long flowing hair, enchanted forest background with ethereal mist, dappled natural light, realistic skin with subtle glow, bow and quiver, face fully visible, serene otherworldly beauty',
  
  elf_male: 'Noble elven warrior in silver armor with elven designs, pointed ears, long hair, mystical forest setting, natural magical lighting, bow or sword, face visible, ageless elegance',
  
  fairy: 'Delicate fairy with translucent wings, flowing dress, magical garden background, soft glowing light, face visible, whimsical enchanting expression',

  // ============================================
  // HISTORICAL WARRIORS
  // ============================================
  
  knight: 'Medieval knight in polished silver plate armor with heraldic symbols, chainmail visible at joints, red or blue cape, castle courtyard with stone walls and banners, natural daylight, realistic metal reflections and battle wear, HELMET REMOVED and held at side, sword in scabbard, face fully visible, noble chivalrous expression',
  
  knight_female: 'Medieval female knight in polished plate armor with heraldic details, chainmail, flowing cape, castle courtyard background, natural lighting, realistic armor texture, HELMET OFF and at side, sword sheathed, face fully visible, noble warrior expression',
  
  samurai: 'Traditional Japanese samurai in ornate lacquered armor (o-yoroi) with red and black colors, silk underlayers visible, katana sword at side, cherry blossom garden setting with temple, soft natural daylight, authentic period details, kabuto helmet removed and placed respectfully, traditional hairstyle, face fully visible, dignified honorable samurai expression',
  
  samurai_female: 'Female samurai warrior (onna-bugeisha) in elegant lamellar armor, silk kimono layers, naginata polearm, cherry blossom background, traditional Japanese garden, natural light, helmet removed, traditional hairstyle, face visible, fierce yet refined expression',
  
  viking: 'Norse warrior in authentic fur-lined leather armor with Norse patterns, braided hair and beard, chainmail, battle axe or sword, snowy Nordic forest background with pine trees, overcast natural light, rugged weathered look, battle-worn authentic details, NO horned helmet (historically accurate), face fully visible, fierce determined Nordic features',
  
  viking_female: 'Norse shield-maiden in fur and leather armor, braided hair, chainmail vest, axe or shield, snowy Nordic landscape, natural cold lighting, warrior build, no helmet obscuring face, face visible, fierce Norse warrior expression',
  
  spartan: 'Ancient Greek Spartan warrior in bronze armor with red cape, bronze helmet with red crest held at side, spear and shield with lambda symbol, ancient Greece background, Mediterranean sunlight, muscular build, NO helmet on head, face visible, warrior discipline',
  
  gladiator: 'Roman gladiator in leather and metal armor, arena background, Colosseum, dramatic lighting, muscular athletic build, helmet removed, face visible, battle-hardened expression',
  
  roman_soldier: 'Roman legionnaire in segmented armor (lorica segmentata), red tunic, Roman fort background, helmet off, gladius sword, face visible, disciplined military bearing',

  // ============================================
  // HISTORICAL & CULTURAL
  // ============================================
  
  pirate: 'Pirate captain in 17th-century long coat with gold trim, leather tricorn hat, cutlass at belt, wooden ship deck background with rigging and sails, Caribbean sea atmosphere, realistic period clothing, face fully visible with weathered features, adventurous roguish expression, possible beard',
  
  pirate_female: 'Female pirate captain in period coat and leather vest, tricorn hat, cutlass, ship deck background, ocean horizon, period-accurate clothing, face visible, fierce independent expression, windswept hair',
  
  pharaoh: 'Ancient Egyptian pharaoh in golden headdress (nemes) with cobra symbol, ornate collar jewelry, white and gold royal garments, pyramid or temple background, warm desert sunlight, documentary historical realism, kohl-lined eyes, face fully visible, regal divine authority expression',
  
  cleopatra: 'Egyptian queen in gold headdress and jewelry, flowing royal garments, palace background, warm lighting, face visible, beautiful commanding presence',
  
  cowboy: 'Old West cowboy in leather vest and worn denim, wide-brimmed hat, leather holster, desert landscape with mesas background, dusty realistic weathered look, sunset golden hour, face fully visible with sun-weathered features, rugged frontier expression',
  
  cowgirl: 'Old West cowgirl in period clothing, cowboy hat, desert landscape, sunset lighting, leather details, face visible, independent frontier spirit',
  
  native_american_chief: 'Native American tribal leader in traditional regalia with feather headdress, beaded clothing, natural landscape, respectful authentic representation, face visible, dignified noble expression',
  
  geisha: 'Traditional Japanese geisha in elegant kimono with intricate patterns, white face makeup, traditional hairstyle with ornaments, Japanese garden or tea house, soft natural light, face visible, graceful refined expression',

  // ============================================
  // PROFESSIONS & MODERN
  // ============================================
  
  ceo: 'Successful CEO in perfectly tailored charcoal suit with silk tie, luxury office setting, Manhattan skyline visible through floor-to-ceiling windows, golden hour light, modern minimalist furniture, face fully visible, confident powerful executive presence, commanding posture',
  
  ceo_female: 'Successful female CEO in elegant tailored suit, luxury corner office, city skyline through windows, sophisticated lighting, face visible, powerful confident presence',
  
  lawyer: 'Professional attorney in formal suit, law library background with legal books, professional lighting, face visible, authoritative intelligent expression',
  
  scientist: 'Research scientist in white lab coat, modern laboratory with equipment, beakers and microscopes, scientific atmosphere, face visible, focused analytical expression',
  
  teacher: 'Dedicated teacher in professional attire, classroom background with chalkboard, educational materials, warm lighting, face visible, inspiring encouraging expression',
  
  architect: 'Professional architect with blueprints, modern office or construction site, drafting tools, creative workspace, face visible, innovative visionary look',

  // ============================================
  // ENTERTAINMENT & ARTS
  // ============================================
  
  rockstar: 'Rock singer in black leather jacket with metal studs, ripped jeans, electric guitar, concert stage background with dramatic lighting, smoke and stage lights, amplifiers, face fully visible, intense passionate performance expression, long hair or styled hair, microphone stand',
  
  rockstar_female: 'Female rock singer in leather outfit, electric guitar, concert stage with lights, energetic atmosphere, face visible, powerful stage presence',
  
  popstar: 'Pop music star in stylish modern outfit, concert stage with colorful lights, backup dancers, energetic performance, face visible, charismatic performer expression',
  
  dj: 'Electronic music DJ at turntables, nightclub background, neon lights, headphones around neck, face visible, focused creative expression',
  
  actor: 'Hollywood actor in elegant attire, red carpet background, camera flashes, glamorous setting, face visible, star quality presence',
  
  dancer: 'Professional dancer in performance attire, dance studio or stage, dynamic pose, dramatic lighting, athletic grace, face visible, expressive artistic emotion',
  
  painter: 'Artist with paint palette and brush, studio with canvases, creative workspace, natural light, paint-stained apron, face visible, creative contemplative expression',
  
  photographer: 'Professional photographer(s) with camera, studio background, photography equipment, creative lighting, face visible, artistic focused look',

  // ============================================
  // ADVENTURE & EXPLORATION
  // ============================================
  
  explorer: 'Adventurer in khaki expedition clothing, jungle or mountain background, rugged gear, natural outdoor lighting, face visible, adventurous determined expression',
  
  archaeologist: 'Field archaeologist in practical expedition wear, ancient ruins background, excavation tools, dusty authentic atmosphere, face visible, scholarly adventurous look',
  
  mountaineer: 'Mountain climber in technical gear, mountain peak background, climbing equipment, dramatic natural lighting, face visible, accomplished determined expression',
  
  diver: 'Scuba diver in wetsuit, mask on top of head or held, ocean or boat background, diving equipment, face visible, adventurous ocean explorer look',
  
  safari_guide: 'Wildlife guide in khaki safari outfit, African savanna background, binoculars, natural lighting, face visible, knowledgeable rugged expression',

  // ============================================
  // SCI-FI & FUTURISTIC
  // ============================================
  
  cyberpunk: 'Cyberpunk character in tech-enhanced clothing, neon-lit futuristic city background, holographic displays, dramatic cyberpunk lighting, face visible, edgy futuristic look',
  
  space_marine: 'Futuristic soldier in advanced armor, sci-fi military base, high-tech weapons, dramatic lighting, helmet off, face visible, elite warrior expression',
  
  robot_engineer: 'Tech engineer in futuristic lab coat, robotics laboratory, advanced machinery, clean tech lighting, face visible, innovative genius expression',
  
  time_traveler: 'Time traveler in mix of period clothing, temporal portal background, mysterious lighting, face visible, wise mysterious expression',

  // ============================================
  // ADDITIONAL CHARACTERS
  // ============================================
  
  vampire: 'Elegant vampire in Victorian clothing, gothic castle background, moonlight and candlelight, pale skin, face visible, mysterious alluring expression',
  
  werewolf_human: 'Werewolf in human form, forest background, moonlit atmosphere, rugged features, face visible, intense wild expression',
  
  superhero_custom: 'Original superhero in unique costume design, city background, heroic pose, dramatic lighting, cape optional, face visible, heroic determined expression',
  
  ninja: 'Ninja warrior in traditional dark gi, Japanese dojo or rooftop, moonlit atmosphere, mask pulled down to reveal face, face fully visible, stealthy focused expression',
  
  monk: 'Martial arts monk in traditional robes, temple background, serene atmosphere, shaved head or traditional hairstyle, face visible, peaceful disciplined expression',
  
  business_professional: 'Business professional in modern suit, corporate office, professional lighting, face visible, confident competent demeanor',
  
  fashion_model: 'Fashion model in designer clothing, runway or studio background, professional fashion photography lighting, face visible, striking confident pose',
  
  athlete_general: 'Professional athlete in sport attire, training facility background, athletic physique, motivational atmosphere, face visible, determined competitive expression',

  // Heroes missing expert prompts
  blackwidow: 'Elite spy/agent in sleek form-fitting black tactical stealth suit with widow\'s bite bracelet devices on wrists, modern high-tech facility background, dramatic low-key lighting, combat-ready stance, NO mask, face fully visible, determined fierce expression, athletic build, spy thriller photography',
  captainmarvel: 'Cosmic superhero in red, blue and gold suit with star emblem, glowing cosmic energy around fists, outer space background with Earth visible below, dynamic flying pose, cosmic lighting with energy glow, NO helmet, face fully visible, powerful confident expression, hair flowing in zero gravity',
  aquaman: 'King of Atlantis in golden scale armor with trident, underwater Atlantis palace background with bioluminescent sea life and golden spires, aquatic blue-green lighting, powerful regal stance, face fully visible, authoritative oceanic expression, aquatic king majesty',
  angel: 'Heavenly angel with large white feathered wings partially extended behind, flowing white and gold celestial robes, ethereal cloud setting with divine rays of light, face fully visible and naturally illuminated, serene compassionate expression, no halo blocking face, divine glow on skin',

  // Professions missing expert prompts
  judge: 'Professional judge in formal black judicial robe, wooden courtroom bench with gavel, flags and seal in background, professional portrait lighting, face fully visible, authoritative dignified expression, courtroom documentary photography',
  accountant: 'Professional accountant in sharp business suit with reading glasses, modern office setting with financial reports and laptop with spreadsheets, clean professional lighting, face fully visible, intelligent focused expression',
  spy: 'Intelligence operative in perfectly tailored dark suit, sleek modern casino or embassy background, low-key dramatic lighting, suave confident stance, face fully visible, no facial coverings, debonair expression, spy thriller photography',
  biker: 'Motorcycle rider in premium black leather biker jacket with studs, custom motorcycle on open highway background, golden sunset lighting, aviator sunglasses pushed up on forehead revealing face fully, rugged windswept expression',
  android: 'Advanced humanoid android with subtle mechanical features integrated into realistic human appearance — visible tech ports at neck and temples, sleek laboratory background, cool blue LED accent lighting, face fully visible with precise synthetic detail, neutral yet expressive android face',
  alien_diplomat: 'Sophisticated alien ambassador with distinctive alien features (tall cranium, iridescent skin, large eyes) in elegant formal intergalactic diplomatic attire, galactic council chamber background, otherworldly blue-purple ambient lighting, face fully visible, composed diplomatic expression',
  jedi: 'Jedi Knight in traditional brown and beige robes with hood down, ignited blue or green lightsaber casting glow, ancient Jedi temple stone columns background, Force-energy dramatic lighting, face fully visible, calm focused expression, wisdom in eyes',

  // Sports missing expert prompts
  runner: 'Elite track and field athlete in aerodynamic competition racing kit, Olympic stadium running track background, mid-sprint dynamic pose or starting blocks, bright stadium arc lighting, face fully visible with fierce competitive expression, athletic build',
  skater: 'Professional skateboarder in urban streetwear hoodie and cargo pants, concrete skate park with graffiti background, mid-trick on skateboard or holding board, natural daylight, face fully visible, focused determined expression, street sports photography',
  swimmer: 'Olympic swimmer in racing swimsuit, poolside at Olympic aquatic center with lane markers, swim cap held in hand revealing face fully, bright aquatic lighting, lean athletic physique, confident champion expression',
  surfer: 'Professional surfer in wetsuit on beach holding surfboard under arm, Pacific ocean with rolling waves at golden sunset, sun-bleached windswept hair, face fully visible with sun-tanned skin, relaxed beach expression, coastal photography',
  tennis: 'Professional tennis player in technical sportswear, outdoor clay or grass court background with net, racket in hand mid-swing or ready stance, natural daylight, face fully visible, focused competitive expression, sports photography',

  // Entertainment missing expert prompts
  pop_star: 'Chart-topping pop star in dazzling colorful sequined stage outfit, concert arena stage with pyrotechnics and laser lights background, holding microphone confidently, face fully visible, charismatic performance expression, massive crowd visible behind',
  movie_director: 'Film director in casual creative attire on movie set, director\'s chair or standing behind camera holding megaphone, film set with lights and crew background, behind-the-scenes lighting, face fully visible, visionary creative expression',
  sculptor: 'Fine art sculptor in dust-covered work clothes, large studio with marble and bronze sculptures background, holding chisel and mallet, stone or clay dust on hands, face fully visible with concentrated artistic expression, art studio photography',

  // Adventure missing expert prompts
  mountaineer: 'Elite mountain climber in full technical alpine gear (down jacket, harness, crampons), summit of snowy mountain peak background, dramatic clouds with sun breaking through, ice axe in hand, goggles pushed up on forehead, face fully visible with snow-dusted skin, triumphant expression',

  // Newer trending characters missing expert prompts
  tech_bro_male: 'Modern tech entrepreneur in premium casual startup attire — quality t-shirt, stylish chinos or jeans, minimal sneakers — holding a MacBook, sleek co-working space or Silicon Valley office background, bright modern natural lighting, face fully visible, confident visionary expression',
  esports_gamer_male: 'Professional esports gamer in team jersey at high-end gaming setup with RGB keyboard and multiple monitors, gaming chair, headset around neck revealing face fully, colorful RGB ambient lighting, intense focused expression, competitive gaming photography',
  lawyer_female: 'Professional female attorney in sharp tailored business suit, law library with legal volumes background, briefcase or legal pad, professional office lighting, face fully visible, authoritative intelligent expression, corporate legal photography',
  scientist_female: 'Female research scientist in white lab coat, modern research laboratory with equipment and beakers background, safety glasses on top of head not covering face, face fully visible, focused analytical expression, science documentary photography',
  architect_female: 'Female architect in modern professional attire, rolling out architectural blueprints on a desk, modern studio with scale models and drafts background, professional lighting, face fully visible, innovative creative expression',
  veterinarian_female: 'Female veterinarian in medical scrubs with stethoscope, veterinary clinic examination room background, NO surgical mask, face fully visible, caring compassionate expression, professional animal healthcare photography',
  mechanic_male: 'Professional auto mechanic in work coveralls with company logo, modern auto repair garage with car on lift background, holding a wrench or diagnostic tool, industrial workshop lighting, face fully visible, skilled confident expression',
  fitness_influencer_male: 'Fitness influencer in premium athletic compression wear, modern gym with weights and equipment background, powerful athletic pose, bright motivational gym lighting, face fully visible, energetic determined expression, fitness photography',
  streetwear_model_male: 'Urban fashion model in high-end designer streetwear — limited sneakers, graphic tee, premium jacket — city street or graffiti wall background, natural daylight, face fully visible, cool confident street style expression',
  coffee_barista_male: 'Artisan barista in stylish cafe apron, high-end espresso machine at specialty coffee bar background, holding a latte art cup, warm cafe lighting, face fully visible, focused creative expression, artisanal cafe photography',
  fashion_influencer_female: 'Fashion content creator in trendy designer outfit, high-fashion boutique or urban street background, natural photography lighting, face fully visible, confident stylish expression, fashion editorial photography',
  yoga_instructor_female: 'Yoga instructor in quality yoga attire, serene yoga studio or outdoor nature setting, graceful yoga pose, soft natural calming light, face fully visible, peaceful mindful expression',
  beauty_vlogger_female: 'Beauty content creator with professional makeup, home studio or beauty space with ring light background, holding makeup brush or product, beauty video lighting, face fully visible with flawless makeup, confident camera-ready expression',
  travel_blogger_female: 'Travel content creator in stylish adventure outfit, stunning scenic travel destination background — mountains or Mediterranean coast, natural photography lighting, face fully visible, adventurous joyful expression, travel photography'
};


const stripBasePrompt = (prompt) => {
  if (!prompt) {
    return '';
  }
  const prefix = `${BASE_PROMPT}\n`;
  return prompt.startsWith(prefix) ? prompt.slice(prefix.length) : prompt;
};

const withRealismSuffix = (prompt) => {
  if (!prompt) {
    return REALISM_SUFFIX;
  }
  return prompt.toLowerCase().includes('grainy texture') ? prompt : `${prompt}, ${REALISM_SUFFIX}`;
};

const applyExpertPrompt = (character) => {
  const override = EXPERT_PROMPTS_BY_ID[character.id];
  const rawPrompt = withRealismSuffix(override ? override : stripBasePrompt(character.prompt));
  return { ...character, prompt: `${BASE_PROMPT}\n${rawPrompt}` };
};

export const CATEGORIES = [
  { id: 'all', name_en: 'All', name_he: 'הכל' },
  { id: 'heroes', name_en: 'Heroes', name_he: 'גיבורים' },
  { id: 'professions', name_en: 'Professions', name_he: 'מקצועות' },
  { id: 'military', name_en: 'Military', name_he: 'צבא' },
  { id: 'sports', name_en: 'Sports', name_he: 'ספורט' },
  { id: 'fantasy', name_en: 'Fantasy', name_he: 'פנטזיה' },
  { id: 'historical', name_en: 'Historical', name_he: 'היסטורי' },
  { id: 'business', name_en: 'Business', name_he: 'עסקים' },
  { id: 'entertainment', name_en: 'Entertainment', name_he: 'בידור' },
  { id: 'scifi', name_en: 'Sci-Fi', name_he: 'מדע בדיוני' },
  { id: 'adventure', name_en: 'Adventure', name_he: 'הרפתקה' },
  { id: 'arts', name_en: 'Arts', name_he: 'אומנות' },
];

export const GENDERS = [
  { id: 'all', name_en: 'All', name_he: 'הכל' },
  { id: 'male', name_en: 'Men', name_he: 'גברים' },
  { id: 'female', name_en: 'Women', name_he: 'נשים' },
];

const RAW_CHARACTERS = [
  // Heroes
  {
    id: 'superman',
    name_en: 'Superman Style',
    name_he: 'סגנון סופרמן',
    category: 'heroes',
    gender: 'all',
    image_url: '/demos/superman.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Kryptonian Hero\nTransform into: Iconic superhero character(s) in blue suit with red cape and S symbol\nBody: Muscular, powerful superhero stance\nSetting: Metropolis skyline, flying pose above the city\nLighting: Bright sunlight, heroic atmosphere\nStyle: Raw documentary superhero photography, 8k photograph, hyper-realistic, practical effects, f/1.8 aperture`
  },
  {
    id: 'batman',
    name_en: 'Batman Style',
    name_he: 'סגנון באטמן',
    category: 'heroes',
    gender: 'all',
    image_url: '/demos/batman.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Dark Knight\nTransform into: Tactical vigilante hero(es) in black suit with cape, no mask, face fully visible\nBody: Athletic, intimidating stance\nSetting: Gotham City rooftop at night, bat signal in sky\nLighting: Dramatic shadows, noir atmosphere\nStyle: Raw crime scene photography, tactical realism, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'wonderwoman',
    name_en: 'Amazon Warrior',
    name_he: 'לוחמת אמזונה',
    category: 'heroes',
    gender: 'all',
    image_url: '/demos/wonderwoman.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Amazonian Warrior\nTransform into: Ancient warrior(s) in red and gold armor\nBody: Strong, confident warrior pose\nSetting: Paradise Island or battlefield\nLighting: Golden hour, epic atmosphere\nStyle: Raw war photography, ancient warrior realism, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'spiderman',
    name_en: 'Spider-Hero',
    name_he: 'גיבור עכביש',
    category: 'heroes',
    gender: 'all',
    image_url: '/demos/spiderman.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Spider-Hero\nTransform into: Web-slinger hero(es) in red and blue tactical suit\nBody: Athletic, agile pose, possibly wall-crawling\nSetting: New York City skyscrapers\nLighting: Golden hour, dramatic urban lighting\nStyle: Raw urban photography, photojournalism style, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'ironman',
    name_en: 'Iron Hero',
    name_he: 'גיבור ברזל',
    category: 'heroes',
    gender: 'all',
    image_url: '/demos/ironman.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Iron Hero\nTransform into: Genius hero(es) in high-tech power armor\nBody: Heroic stance, repulsors ready\nSetting: High-tech laboratory or mid-flight\nLighting: Arc reactor glow, futuristic lighting\nStyle: Raw industrial photography, realistic metal texture, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'blackwidow',
    name_en: 'Elite Spy',
    name_he: 'מרגלת מובחרת',
    category: 'heroes',
    gender: 'all',
    image_url: '/demos/blackwidow.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Elite Spy\nTransform into: Elite tactical agent(s) in black stealth suit\nBody: Athletic, combat-ready stance\nSetting: Sleek modern facility or action scene\nLighting: Dramatic action lighting\nStyle: Raw tactical photography, spy thriller realism, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'captainmarvel',
    name_en: 'Cosmic Hero',
    name_he: 'גיבורה קוסמית',
    category: 'heroes',
    gender: 'all',
    image_url: '/demos/captainmarvel.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Cosmic Hero\nTransform into: Cosmic superhero(es) in red, blue and gold suit\nBody: Powerful, confident stance with glowing fists\nSetting: Space or flying above Earth\nLighting: Cosmic energy glow\nStyle: Raw space photography, NASA footage style, 8k photograph, f/1.8 aperture`
  },
  
  // Professions
  {
    id: 'doctor',
    name_en: 'Doctor',
    name_he: 'רופאים',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/doctor.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Doctor\nTransform into: Professional physician(s) in white coat with stethoscope\nBody: Confident, caring posture\nSetting: Modern hospital or medical office\nLighting: Clean, professional lighting\nStyle: Raw medical documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'chef',
    name_en: 'Chef',
    name_he: 'שפים',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/chef.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Master Chef\nTransform into: Professional chef(s) in white uniform with chef's hat\nBody: Confident kitchen stance, holding knife or pan\nSetting: Luxury restaurant kitchen\nLighting: Warm kitchen lighting\nStyle: Kitchen documentary photography, raw photo, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'pilot',
    name_en: 'Pilot',
    name_he: 'טייסים',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/pilot.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Commercial Pilot\nTransform into: Airline captain(s) in pilot uniform with epaulettes\nBody: Professional, commanding stance\nSetting: Airplane cockpit or airport\nLighting: Professional aviation lighting\nStyle: Aviation documentary photography, raw photo, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'astronaut',
    name_en: 'Astronaut',
    name_he: 'אסטרונאוטים',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/astronaut.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Astronaut\nTransform into: Space explorer(s) in NASA spacesuit\nBody: Heroic space pose\nSetting: Space station or lunar surface with Earth in background\nLighting: Space lighting with Earth glow\nStyle: Raw space documentary photography, NASA footage, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'firefighter',
    name_en: 'Firefighter',
    name_he: 'כבאים',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/firefighter.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Firefighter\nTransform into: Brave firefighter(s) in full gear, helmet held at the side, face fully visible\nBody: Heroic, ready-for-action stance\nSetting: Fire scene or fire station\nLighting: Dramatic fire lighting or station lights\nStyle: Raw fire scene documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'police',
    name_en: 'Police Officer',
    name_he: 'שוטרים',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/police.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Police Officer\nTransform into: Professional police officer(s) in modern uniform\nBody: Confident standing pose, face fully visible, no helmet\nSetting: City street or police station exterior\nLighting: Natural daylight, realistic street lighting\nStyle: Raw documentary photography, photojournalism, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'judge',
    name_en: 'Judge',
    name_he: 'שופט/ת',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/judge.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Judge\nTransform into: Professional judge(s) in black robe\nBody: Seated or standing confidently, face fully visible\nSetting: Courtroom with wooden bench and flags\nLighting: Natural courtroom lighting, realistic\nStyle: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },

  // Military
  {
    id: 'soldier',
    name_en: 'Soldier',
    name_he: 'חיילים',
    category: 'military',
    gender: 'all',
    image_url: '/demos/soldier.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Elite Soldier\nTransform into: Male special forces operator in tactical gear\nBody: Combat-ready stance with weapon\nSetting: Military base or tactical environment\nLighting: Dramatic military lighting\nStyle: Raw military documentary photography, war correspondent style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'soldier_female',
    name_en: 'Soldier',
    name_he: 'חיילת',
    category: 'military',
    gender: 'female',
    image_url: '/demos/soldier_female.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Elite Soldier\nTransform into: Female special forces operator in tactical gear\nBody: Combat-ready stance with weapon\nSetting: Military base or tactical environment\nLighting: Dramatic military lighting\nStyle: Raw military documentary photography, war correspondent style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'fighter_pilot',
    name_en: 'Fighter Pilot',
    name_he: 'טייס קרב',
    category: 'military',
    gender: 'all',
    image_url: '/demos/fighter_pilot.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Fighter Pilot\nTransform into: Top Gun style pilot(s) in flight suit, helmet held at the side, face fully visible\nBody: Confident pilot pose\nSetting: Fighter jet cockpit or hangar with jets\nLighting: Aviation lighting\nStyle: Raw aviation documentary photography, air force archives, 8k photograph, hyper-realistic, f/1.8 aperture`
  },

  // Sports
  {
    id: 'soccer',
    name_en: 'Soccer Star',
    name_he: 'כוכבי כדורגל',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/soccer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Soccer Star\nTransform into: Professional soccer player(s) in team jersey\nBody: Athletic, dynamic soccer pose with ball\nSetting: Stadium with crowd and green pitch\nLighting: Stadium floodlights, evening atmosphere\nStyle: Raw sports action photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'basketball',
    name_en: 'Basketball Star',
    name_he: 'כוכבי כדורסל',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/basketball.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Basketball Star\nTransform into: Pro basketball player(s) in jersey, mid-action\nBody: Athletic, dynamic pose\nSetting: Basketball arena with crowd\nLighting: Arena lights, action shot\nStyle: Raw sports action photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'boxer',
    name_en: 'Boxer',
    name_he: 'מתאגרפים',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/boxer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Champion Boxer\nTransform into: Professional boxer(s) with gloves and sports gear\nBody: Fighting stance, powerful pose\nSetting: Boxing ring with lights\nLighting: Dramatic ring lighting\nStyle: Raw boxing documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'runner',
    name_en: 'Runner',
    name_he: 'אצנים',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/runner.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Runner\nTransform into: Professional track runner(s) in competition kit\nBody: Sprinting pose or starting blocks\nSetting: Stadium running track\nLighting: Stadium lighting, action shot\nStyle: Raw sports action photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'skater',
    name_en: 'Skater',
    name_he: 'סקייטרים',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/skater.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Skateboarder\nTransform into: Professional skateboarder(s) in streetwear\nBody: Dynamic skate pose with board, face visible\nSetting: Urban skate park\nLighting: Natural daylight, urban atmosphere\nStyle: Raw action sports photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'swimmer',
    name_en: 'Swimmer',
    name_he: 'שחיינים',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/swimmer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Competitive Swimmer\nTransform into: Professional swimmer(s) in racing suit\nBody: Poolside athletic stance, swim cap held in hand, face visible\nSetting: Olympic swimming pool with lanes\nLighting: Bright aquatic lighting\nStyle: Raw sports action photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'surfer',
    name_en: 'Surfer',
    name_he: 'גולשים',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/surfer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Surfer\nTransform into: Professional surfer(s) in wetsuit carrying surfboard\nBody: Athletic surfer stance, board under arm, face visible\nSetting: Beach with rolling waves\nLighting: Golden hour coastal lighting\nStyle: Raw surf sports photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'tennis',
    name_en: 'Tennis Player',
    name_he: 'טניסאים',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/tennis.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Tennis Player\nTransform into: Professional tennis player(s) in sportswear with racket\nBody: Ready stance or mid-swing with racket\nSetting: Tennis court with net\nLighting: Bright daylight, outdoor court lighting\nStyle: Raw sports action photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'diver',
    name_en: 'Diver',
    name_he: 'צוללנים',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/diver.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Scuba Diver\nTransform into: Professional scuba diver(s) in wetsuit with oxygen tank\nBody: Standing on boat deck, mask and snorkel held at side, face fully visible\nSetting: Ocean shoreline or dive boat\nLighting: Bright natural daylight, coastal atmosphere\nStyle: Raw ocean sports documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },

  // Fantasy
  {
    id: 'wizard',
    name_en: 'Wizard/Witch',
    name_he: 'קוסמים/מכשפות',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/wizard.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Powerful Magic User\nTransform into: Mystical wizard/witch character(s) in robes with staff\nBody: Casting spell pose, magical energy\nSetting: Magical tower or enchanted forest\nLighting: Magical glow, mystical atmosphere\nStyle: Raw fantasy documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'knight',
    name_en: 'Knight',
    name_he: 'אבירים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/knight.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Noble Knight\nTransform into: Medieval knight warrior(s) in shining armor with sword\nBody: Heroic stance, sword drawn\nSetting: Castle or battlefield\nLighting: Epic medieval lighting\nStyle: Raw medieval documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'pirate',
    name_en: 'Pirate',
    name_he: 'פיראטים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/pirate.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Pirate Captain\nTransform into: Swashbuckling pirate character(s) with hat and sword\nBody: Adventurous captain stance\nSetting: Pirate ship deck with ocean\nLighting: Caribbean sunset\nStyle: Raw historical documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'ninja',
    name_en: 'Ninja',
    name_he: 'נינג\'ה',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/ninja.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Shadow Ninja\nTransform into: Stealthy ninja character(s) in black outfit with katana, NO MASK, face fully visible\nBody: Combat stance, ready to strike\nSetting: Japanese temple or rooftop at night\nLighting: Moonlight, mysterious atmosphere\nStyle: Raw historical documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'viking',
    name_en: 'Viking',
    name_he: 'ויקינגים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/viking.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Viking Warrior\nTransform into: Fierce Viking warrior(s) with fur and leather armor, axe, no helmet, face fully visible\nBody: Battle-ready warrior stance\nSetting: Snowy Nordic landscape or longship\nLighting: Northern dramatic lighting\nStyle: Raw historical documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'elf',
    name_en: 'Elf',
    name_he: 'אלפים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/elf.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Elven Noble\nTransform into: Elegant elven character(s) with pointed ears and flowing robes\nBody: Graceful, ethereal pose\nSetting: Enchanted forest with glowing lights\nLighting: Magical soft lighting\nStyle: Raw fantasy documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'fairy',
    name_en: 'Fairy',
    name_he: 'פיה',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/girls/fairy_female.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Magical Fairy\nTransform into: Beautiful fairy character(s) with delicate wings and sparkly dress\nBody: Graceful, floating pose\nSetting: Enchanted garden with flowers\nLighting: Magical sparkle lighting\nStyle: Raw fantasy documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },

  // Historical
  {
    id: 'pharaoh',
    name_en: 'Pharaoh',
    name_he: 'פרעה',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/pharaoh.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Egyptian Pharaoh\nTransform into: Mighty pharaoh character(s) with crown and royal attire\nBody: Regal, powerful stance\nSetting: Egyptian palace with hieroglyphics\nLighting: Golden royal lighting\nStyle: Raw historical documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'samurai',
    name_en: 'Samurai',
    name_he: 'סמוראים',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/samurai.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Samurai Warrior\nTransform into: Honorable samurai warrior(s) in full armor with katana\nBody: Disciplined warrior stance\nSetting: Japanese castle or cherry blossom garden\nLighting: Natural Japanese daylight\nStyle: Raw historical documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'cowboy',
    name_en: 'Cowboy/Cowgirl',
    name_he: 'קאובוי/גירל',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/cowboy.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Western Character\nTransform into: Rugged western character(s) with hat, boots, and revolver\nBody: Classic western standoff pose\nSetting: Wild West town or desert\nLighting: Dusty golden hour\nStyle: Raw historical documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },

  // Business
  {
    id: 'ceo',
    name_en: 'CEO',
    name_he: 'מנכ״ל/ית',
    category: 'business',
    gender: 'all',
    image_url: '/demos/ceo.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: CEO\nTransform into: Powerful CEO character(s) in luxury suit\nBody: Confident executive stance\nSetting: Corner office with city skyline view\nLighting: Professional office lighting\nStyle: Raw corporate documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'accountant',
    name_en: 'Accountant',
    name_he: 'רואי חשבון',
    category: 'business',
    gender: 'all',
    image_url: '/demos/accountant.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Professional Accountant\nTransform into: Sharp accountant(s) in formal business attire\nBody: Professional, focused pose\nSetting: Modern office with documents and computer\nLighting: Clean office lighting\nStyle: Raw corporate documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },

  // Entertainment
  {
    id: 'rockstar',
    name_en: 'Rock Star',
    name_he: 'רוקסטאר',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/rockstar.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Rock Star\nTransform into: Legendary rock star(s) with guitar\nBody: Performance pose on stage\nSetting: Concert stage with lights and crowd\nLighting: Concert stage lighting, dramatic\nStyle: Raw concert documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'actor',
    name_en: 'Movie Star',
    name_he: 'כוכבי קולנוע',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/actor.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Hollywood Star\nTransform into: A-list movie star(s) on red carpet in formal wear\nBody: Charismatic celebrity pose\nSetting: Red carpet premiere with photographers\nLighting: Red carpet flash photography\nStyle: Raw celebrity documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'dj',
    name_en: 'DJ',
    name_he: 'די.ג׳יי',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/dj.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Superstar DJ\nTransform into: World-famous DJ(s) at the turntables\nBody: Energetic performance pose\nSetting: Massive festival stage with crowd\nLighting: EDM concert lighting, lasers\nStyle: Raw concert documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },

  // Cool
  {
    id: 'spy',
    name_en: 'Secret Agent',
    name_he: 'סוכן חשאי',
    category: 'business',
    gender: 'all',
    image_url: '/demos/spy.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Secret Agent\nTransform into: Elite spy character(s) in tailored suit\nBody: Suave, confident spy pose\nSetting: Casino or exotic location\nLighting: Low-key documentary lighting\nStyle: Raw spy thriller documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'biker',
    name_en: 'Biker',
    name_he: 'אופנוענים',
    category: 'adventure',
    gender: 'all',
    image_url: '/demos/biker.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Motorcycle Rider\nTransform into: Cool biker(s) in leather jacket\nBody: Confident rider stance\nSetting: Open road with motorcycle\nLighting: Golden hour highway\nStyle: Raw motorcycle documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },

  // Sci-Fi
  {
    id: 'android',
    name_en: 'Android',
    name_he: 'אנדרואיד',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/android.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Advanced Android\nTransform into: Humanoid android(s) with visible tech components\nBody: Perfect synthetic human pose\nSetting: Futuristic laboratory\nLighting: Clean sci-fi lighting with blue accents\nStyle: Raw sci-fi documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'alien_diplomat',
    name_en: 'Alien Diplomat',
    name_he: 'דיפלומט חייזרי',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/alien-diplomat.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Alien Ambassador\nTransform into: Sophisticated alien diplomat(s) in formal attire\nBody: Dignified diplomatic pose\nSetting: Galactic council or spacecraft\nLighting: Otherworldly ambient lighting\nStyle: Raw sci-fi documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'jedi',
    name_en: 'Jedi',
    name_he: 'ג׳דיי',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/jedi.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Jedi Master\nTransform into: Wise Jedi character(s) in robes with lightsaber\nBody: Force-wielding stance\nSetting: Jedi temple or starship\nLighting: Lightsaber glow, mystical\nStyle: Raw sci-fi documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },

  // Adventure
  {
    id: 'explorer',
    name_en: 'Explorer',
    name_he: 'חוקרים',
    category: 'adventure',
    gender: 'all',
    image_url: '/demos/explorer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: World Explorer\nTransform into: Adventurous explorer(s) with gear and hat\nBody: Ready for expedition pose\nSetting: Jungle or ancient ruins\nLighting: Natural jungle daylight\nStyle: Raw adventure documentary photography, National Geographic style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'archaeologist',
    name_en: 'Archaeologist',
    name_he: 'ארכיאולוגים',
    category: 'adventure',
    gender: 'all',
    image_url: '/demos/archaeologist.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Archaeologist\nTransform into: Field archaeologist(s) in expedition gear with hat and satchel\nBody: Adventurous discovery pose\nSetting: Ancient temple or dig site\nLighting: Dramatic adventure lighting\nStyle: Raw adventure documentary photography, National Geographic style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },

  // Additional characters with uploaded images
  {
    id: 'angel',
    name_en: 'Angel',
    name_he: 'מלאך',
    category: 'fantasy',
    image_url: '/demos/angel.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Divine Angel\nTransform into: Heavenly angel with white wings and robes\nBody: Serene, celestial pose\nSetting: Clouds and divine light\nLighting: Ethereal heavenly glow\nStyle: Raw fantasy documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'aquaman',
    name_en: 'Aquaman',
    name_he: 'אקווהמן',
    category: 'heroes',
    image_url: '/demos/aquaman.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Aquaman\nTransform into: King of Atlantis in golden and green armor with trident\nBody: Powerful aquatic warrior stance\nSetting: Underwater Atlantis kingdom\nLighting: Underwater mystical lighting\nStyle: Raw underwater documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },

  // TRENDING CHARACTERS - 2024/2025 Styles
  {
    id: 'tech_bro_male',
    name_en: 'Tech Bro',
    name_he: 'טק ברו',
    category: 'business',
    gender: 'male',
    image_url: '/demos/tech-bro.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Tech Bro\nTransform into: Modern tech entrepreneur in casual startup attire\nBody: Confident pose with laptop or tablet\nSetting: Modern co-working space or tech office\nLighting: Natural office lighting, professional atmosphere\nStyle: Raw corporate documentary photography, Silicon Valley style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'esports_gamer_male',
    name_en: 'ESports Gamer',
    name_he: 'גיימר איספורטס',
    category: 'entertainment',
    gender: 'male',
    image_url: '/demos/esports-gamer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: ESports Gamer\nTransform into: Professional gamer in gaming chair with headset\nBody: Focused gaming pose, hands on keyboard/mouse\nSetting: Gaming setup with RGB lighting, multiple monitors\nLighting: Colorful RGB lighting, intense gaming atmosphere\nStyle: Raw gaming documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'streetwear_model_male',
    name_en: 'Streetwear Model',
    name_he: 'דוגמן סטריטוויר',
    category: 'arts',
    gender: 'male',
    image_url: '/demos/streetwear-model.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Streetwear Model\nTransform into: Urban fashion model in trendy streetwear\nBody: Casual confident pose, stylish street fashion\nSetting: Urban city street or graffiti wall\nLighting: Natural daylight, urban photography style\nStyle: Raw street fashion documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'fitness_influencer_male',
    name_en: 'Fitness Influencer',
    name_he: 'איש כושר ומשפיע',
    category: 'sports',
    gender: 'male',
    image_url: '/demos/fitness-influencer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Fitness Influencer\nTransform into: Modern fitness personality in athletic wear\nBody: Athletic pose, fitness model physique\nSetting: Modern gym or fitness studio\nLighting: Bright gym lighting, motivational atmosphere\nStyle: Raw fitness documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'coffee_barista_male',
    name_en: 'Coffee Barista',
    name_he: 'בריסטה קפה',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/coffee-barista.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Coffee Barista\nTransform into: Artisanal coffee maker in modern cafe attire\nBody: Professional barista pose, making coffee\nSetting: Trendy coffee shop with espresso machine\nLighting: Warm cafe lighting, cozy atmosphere\nStyle: Raw cafe documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'fashion_influencer_female',
    name_en: 'Fashion Influencer',
    name_he: 'משפיענית אופנה',
    category: 'entertainment',
    gender: 'female',
    image_url: '/demos/girls/fashion-influencer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Fashion Influencer\nTransform into: Style influencer in trendy fashion outfit\nBody: Confident fashion pose, stylish appearance\nSetting: Fashion boutique or urban street style location\nLighting: Natural fashion photography lighting\nStyle: Raw fashion documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'yoga_instructor_female',
    name_en: 'Yoga Instructor',
    name_he: 'מדריכת יוגה',
    category: 'sports',
    gender: 'female',
    image_url: '/demos/girls/yoga-instructor.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Yoga Instructor\nTransform into: Wellness guru in yoga attire\nBody: Graceful yoga pose, peaceful demeanor\nSetting: Serene yoga studio or outdoor nature setting\nLighting: Soft natural light, calming atmosphere\nStyle: Raw yoga documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'beauty_vlogger_female',
    name_en: 'Beauty Vlogger',
    name_he: 'יוטיוברית איפור',
    category: 'entertainment',
    gender: 'female',
    image_url: '/demos/girls/beauty-vlogger.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Beauty Vlogger\nTransform into: Makeup and beauty content creator\nBody: Professional pose with makeup tools\nSetting: Modern beauty studio with ring light\nLighting: Professional beauty video lighting\nStyle: Raw beauty documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'coffee_artist_female',
    name_en: 'Coffee Artist',
    name_he: 'אמנית קפה',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/girls/coffee-artist.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Coffee Artist\nTransform into: Specialty coffee maker with latte art skills\nBody: Professional barista pose creating latte art\nSetting: Trendy artisanal coffee shop\nLighting: Warm cafe lighting, artistic atmosphere\nStyle: Raw cafe documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'travel_blogger_female',
    name_en: 'Travel Blogger',
    name_he: 'בלוגרית מסעות',
    category: 'adventure',
    gender: 'female',
    image_url: '/demos/girls/travel-blogger.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Travel Blogger\nTransform into: Adventure travel content creator\nBody: Adventurous pose with travel gear\nSetting: Scenic travel location or airport\nLighting: Natural travel photography lighting\nStyle: Raw travel documentary photography, National Geographic style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  // NEW CHARACTERS GENERATED WITH POLLINATIONS API
  {
    id: 'photographer_male',
    name_en: 'Photographer',
    name_he: 'צלם',
    category: 'arts',
    gender: 'male',
    image_url: '/demos/photographer-.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Photographer(s)\nTransform into: Professional photographer(s) with camera equipment\nBody: Artistic photography pose with professional camera\nSetting: Photo studio or outdoor photography location\nLighting: Professional photography lighting setup\nStyle: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'mechanic_male',
    name_en: 'Car Mechanic',
    name_he: 'מכונאי רכב',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/mechanic.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Car Mechanic\nTransform into: Professional auto mechanic in work attire\nBody: Confident mechanic pose with tools\nSetting: Modern auto repair garage\nLighting: Workshop lighting, industrial atmosphere\nStyle: Raw industrial documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'firefighter_male',
    name_en: 'Firefighter',
    name_he: 'כבאי',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/firefighter.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Firefighter\nTransform into: Professional firefighter in uniform\nBody: Heroic firefighter pose with equipment\nSetting: Fire station or emergency scene\nLighting: Dramatic emergency lighting\nStyle: Raw fire scene documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'lawyer_female',
    name_en: 'Lawyer',
    name_he: 'עורכת דין',
    category: 'business',
    gender: 'female',
    image_url: '/demos/lawyer_female.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Lawyer\nTransform into: Professional lawyer in business attire\nBody: Confident legal professional pose\nSetting: Modern law office or courtroom\nLighting: Professional office lighting\nStyle: Raw corporate documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'scientist_female',
    name_en: 'Scientist',
    name_he: 'מדענית',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/scientist_female.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Scientist\nTransform into: Research scientist in lab attire\nBody: Professional scientist pose with lab equipment, safety glasses only, NO MASK, face fully visible\nSetting: Modern laboratory or research facility\nLighting: Laboratory lighting, scientific atmosphere\nStyle: Raw science documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'architect_female',
    name_en: 'Architect',
    name_he: 'אדריכלית',
    category: 'arts',
    gender: 'female',
    image_url: '/demos/architect_female.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Architect\nTransform into: Professional architect with design tools\nBody: Creative architect pose with blueprints, face fully visible\nSetting: Modern architecture studio\nLighting: Professional studio lighting\nStyle: Raw architectural documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'veterinarian_female',
    name_en: 'Veterinarian',
    name_he: 'וטרינרית',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/veterinarian_female.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Veterinarian\nTransform into: Animal doctor in medical attire\nBody: Caring veterinarian pose with animal, NO MASK, face fully visible\nSetting: Modern veterinary clinic\nLighting: Clinical veterinary lighting\nStyle: Raw medical documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  // CUSTOM CHARACTERS GENERATED WITH POLLINATIONS API - BASE IMAGE: STRYX MODEL
  {
    id: 'noir_detective_male',
    name_en: 'Noir Detective',
    name_he: 'בלש נואר',
    category: 'entertainment',
    gender: 'male',
    image_url: '/demos/noir-detective.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Noir Detective\nTransform into: 1940s style private investigator\nBody: Wearing a classic trench coat and a fedora hat (pushed back to show face), holding a vintage magnifying glass\nSetting: Rain-slicked city alleyway at night, dim street lamps\nLighting: High-contrast low-key lighting, moody shadows\nStyle: Raw documentary photography, monochrome black-and-white, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'tech_billionaire_male',
    name_en: 'Tech Billionaire',
    name_he: 'טייקון הייטק',
    category: 'business',
    gender: 'male',
    image_url: '/demos/tech-billionaire.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Tech CEO\nTransform into: Modern visionary billionaire in a high-end minimalist setting\nBody: Wearing a premium black turtleneck or a sleek modern blazer, confident standing pose\nSetting: Luxury glass office overlooking a futuristic city skyline at twilight\nLighting: Soft professional studio lighting, cool blue and purple ambient tones\nStyle: Raw corporate documentary photography, executive portrait style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'greek_philosopher_male',
    name_en: 'Greek Philosopher',
    name_he: 'פילוסוף יווני',
    category: 'historical',
    gender: 'male',
    image_url: '/demos/greek-philosopher.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Greek Philosopher\nTransform into: Wise scholar from ancient Athens\nBody: Draped in a high-quality white and gold toga (himation), holding an ancient scroll\nSetting: Marble temple balcony (Parthenon style) with olive trees in the background\nLighting: Bright, warm Mediterranean sunlight\nStyle: Raw historical documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'cyberpunk_racer_male',
    name_en: 'Cyberpunk Racer',
    name_he: 'נהג מרוצים עתידני',
    category: 'scifi',
    gender: 'male',
    image_url: '/demos/cyberpunk-racer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Cyberpunk Street Racer\nTransform into: High-tech driver from a neon-lit future\nBody: Wearing a sleek carbon-fiber racing suit with glowing LED accents\nSetting: Futuristic garage filled with high-tech tools and a glowing sports car\nLighting: Vibrant neon pink and cyan lighting, reflection of lights on the suit\nStyle: Raw sci-fi documentary photography, neon-lit documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'forest_ranger_male',
    name_en: 'Forest Ranger',
    name_he: 'סייר יערות',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/forest-ranger.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Forest Ranger / Park Warden\nTransform into: Professional wilderness protector\nBody: Wearing a tactical outdoor uniform with a utility vest and binoculars around the neck\nSetting: Deep pine forest with sunlight breaking through the trees (God rays)\nLighting: Natural dappled sunlight, soft outdoor atmosphere\nStyle: Raw nature documentary photography, National Geographic style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'royal_aristocrat_male',
    name_en: 'Royal Aristocrat',
    name_he: 'אציל מהמאה ה-18',
    category: 'historical',
    gender: 'male',
    image_url: '/demos/royal-aristocrat.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: 18th Century Nobleman\nTransform into: Royal figure in a grand ballroom\nBody: Wearing an ornate velvet frock coat with gold embroidery and lace cravat\nSetting: Opulent palace ballroom with crystal chandeliers and gold mirrors\nLighting: Warm candlelight glow, glittering atmosphere\nStyle: Raw historical documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'sushi_chef_male',
    name_en: 'Master Sushi Chef',
    name_he: 'שף סושי',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/sushi-chef.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Sushi Master\nTransform into: Professional Japanese chef in a high-end restaurant\nBody: Wearing a traditional white chef's Gi and a clean headband (Hachimaki), holding a professional knife\nSetting: Minimalist upscale sushi bar with wooden textures\nLighting: Sharp, focused bright lighting on the foreground\nStyle: Raw culinary documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'olympic_fencer_male',
    name_en: 'Olympic Fencer',
    name_he: 'סייף אולימפי',
    category: 'sports',
    gender: 'male',
    image_url: '/demos/olympic-fencer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Fencer\nTransform into: Professional athlete in a fencing match\nBody: Wearing a full white fencing suit, holding a foil (sword), the mask is held under the arm to keep the face visible\nSetting: Professional sports arena with a blurred crowd in the background\nLighting: High-contrast athletic arena lighting\nStyle: Raw sports action photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'steampunk_inventor_male',
    name_en: 'Steampunk Inventor',
    name_he: 'ממציא סטימפאנק',
    category: 'fantasy',
    gender: 'male',
    image_url: '/demos/steampunk-inventor.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Steampunk Inventor\nTransform into: Victorian-era genius with mechanical gadgets\nBody: Wearing a leather vest, white shirt with rolled sleeves, and brass goggles (on the forehead, not eyes)\nSetting: Workshop filled with brass gears, steam pipes, and glowing lightbulbs\nLighting: Warm amber glow, smoky atmosphere\nStyle: Raw fantasy documentary photography, steampunk workshop documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'western_outlaw_male',
    name_en: 'Wild West Outlaw',
    name_he: 'קאובוי בצהרי היום',
    category: 'historical',
    gender: 'male',
    image_url: '/demos/western-outlaw.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Wild West Outlaw\nTransform into: Rugged gunslinger from the 1800s\nBody: Wearing a dusty leather duster coat, a bandana around the neck, and a cowboy hat\nSetting: Old West desert town at sunset, dusty main street\nLighting: Golden hour lighting, warm orange and red tones\nStyle: Raw historical documentary photography, western style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  // SECOND BATCH OF CUSTOM CHARACTERS GENERATED WITH POLLINATIONS API - BASE IMAGE: STRYX MODEL
  {
    id: 'fire_elemental_knight',
    name_en: 'Fire Elemental Knight',
    name_he: 'אבירי יסוד האש',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/fire-elemental-knight.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Fire Deity\nTransform into: Powerful being(s) made of embers and molten armor\nBody: Wearing intricate obsidian armor with glowing lava cracks, holding a sword made of pure flame, NO MASK, face fully visible\nSetting: An erupting volcano forge, sparks and ash floating in the air\nLighting: Intense orange and red glow from below, dramatic backlighting\nStyle: Raw fantasy documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'cyber_neon_shinobi',
    name_en: 'Cyber-Neon Shinobi',
    name_he: 'סייבר-נינג\'ה',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/cyber-neon-shinobi.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Cyberpunk Ninja\nTransform into: High-tech assassin(s) from the year 2099\nBody: Sleek tactical carbon-fiber suit with glowing neon-blue circuits, tech-katana strapped to the back, face fully visible, NO MASK\nSetting: A rainy rooftop in a futuristic Tokyo, neon signs reflecting in puddles\nLighting: Cool blue and hot pink neon lighting, moody low-key shadows\nStyle: Raw documentary photography, neon-lit documentary photography, 8k photograph, hyper-realistic`
  },
  {
    id: 'frost_king',
    name_en: 'The Frost Monarch',
    name_he: 'שליטי הקרח',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/frost-king.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Ice Monarch\nTransform into: Ruler(s) of the frozen northern realms\nBody: Heavy fur cloak over silver armor, skin has a subtle frost texture, a crown made of jagged ice crystals\nSetting: A throne room made of solid ice, blizzard swirling in the background\nLighting: Ethereal cold blue light, shimmering reflections on ice surfaces\nStyle: Raw fantasy documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'golden_statue',
    name_en: 'The Midas Touch',
    name_he: 'פסל זהב חי',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/golden-statue.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Golden God/Goddess\nTransform into: Living masterpiece(s) made of solid 24K gold\nBody: The person's skin is smooth polished gold with intricate divine engravings, wearing royal silk drapes\nSetting: A celestial treasury filled with golden treasures and white marble columns\nLighting: Bright, shimmering light reflecting off the gold skin, high-specular highlights\nStyle: Raw fashion documentary photography, vogue style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'urban_sorcerer',
    name_en: 'Urban Sorcerer',
    name_he: 'קוסמים מודרניים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/urban-sorcerer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Urban Sorcerer\nTransform into: Wizard(s) living in the modern world\nBody: Casual stylish street clothes (hoodie/leather jacket) but with glowing magical tattoos on arms, hands casting a swirl of mystical energy\nSetting: A dark city library with floating books and magical sparks\nLighting: Bright magical energy glow from the hands illuminating the face\nStyle: Raw documentary photography, urban night photography, 8k photograph, hyper-realistic`
  },
  {
    id: 'stardust_explorer',
    name_en: 'Star-Dust Explorer',
    name_he: 'חלוצי חלל',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/stardust-explorer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Galactic Space Pioneer\nTransform into: Heroic space explorer(s) in a futuristic setting\nBody: Wearing a detailed, high-tech sci-fi explorer suit with glowing elements and tactical gear (NO HELMET, face fully visible)\nSetting: A futuristic spaceship bridge with a view of a nebula outside, or an alien planet landscape\nLighting: Clean sci-fi lighting, natural color contrast, realistic atmosphere\nStyle: Raw space documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'wasteland_survivor',
    name_en: 'Wasteland Survivor',
    name_he: 'שורדים אפוקליפטיים',
    category: 'adventure',
    gender: 'all',
    image_url: '/demos/wasteland-survivor.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Post-Apocalyptic Warrior\nTransform into: Rugged survivor(s) in a "Mad Max" style world\nBody: Distressed leather gear, tactical belts, shoulder pauldrons made of scrap metal, face has light dust/dirt smudges but NO MASK\nSetting: A vast desert wasteland with wrecked rusted cars in the distance\nLighting: Harsh golden-hour sun, dusty atmosphere\nStyle: Raw documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'atlantean_prince',
    name_en: 'Atlantean Royal',
    name_he: 'אצולת המצולות',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/atlantean-prince.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Underwater Royalty\nTransform into: Leader(s) of a hidden underwater civilization\nBody: Armor made of iridescent sea shells and scales, glowing bioluminescent patterns on the skin\nSetting: An underwater coral palace with schools of fish and sunbeams piercing through the water\nLighting: Aquatic blue-green lighting, shimmering "caustic" water reflections on the face\nStyle: Raw underwater documentary photography, hyper-realistic, 8k photograph, f/1.8 aperture`
  },
  {
    id: 'clockwork_automaton',
    name_en: 'Clockwork Automaton',
    name_he: 'מאסטר השעונים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/clockwork-automaton.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Steampunk Android\nTransform into: Human-mechanical hybrid(s) from the Victorian era\nBody: Visible brass gears and clockwork mechanisms integrated into the neck and shoulders, wearing a dapper velvet vest\nSetting: A laboratory filled with ticking clocks, steam, and brass instruments\nLighting: Warm sepia tones, soft glowing vacuum tubes\nStyle: Raw fantasy documentary photography, steampunk workshop documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'forest_guardian_spirit',
    name_en: 'Forest Guardian Spirit',
    name_he: 'רוחות היער',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/forest-guardian-spirit.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Nature Deity\nTransform into: Mystical protector(s) of the ancient woods\nBody: Clothes woven from living leaves and roots, small glowing forest sprites hovering around\nSetting: A magical enchanted forest with giant glowing mushrooms and ancient trees\nLighting: Soft "God rays" filtering through leaves, magical green and gold sparkles\nStyle: Raw nature documentary photography, National Geographic style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  // NEW BATCH - EXPANSION TO 100+ CHARACTERS
  {
    id: 'cyberpunk_soldier',
    name_en: 'Cyberpunk Soldier',
    name_he: 'חייל סייברפאנק',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/cyberpunk-hacker.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Cyberpunk Soldier\nTransform into: Elite futuristic soldier(s) with cybernetic enhancements\nBody: Wearing high-tech tactical armor with glowing neon accents, holding a futuristic energy rifle, NO HELMET, face fully visible\nSetting: Dystopian city ruins at night with neon signs\nLighting: Cyan and magenta neon lighting, moody atmosphere\nStyle: Raw sci-fi documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'renaissance_painter',
    name_en: 'Renaissance Painter',
    name_he: 'צייר רנסנס',
    category: 'arts',
    gender: 'all',
    image_url: '/demos/renaissance.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Renaissance Master\nTransform into: Classical artist(s) from the Renaissance era\nBody: Wearing period-accurate linen smock stained with paint, holding a palette and brush\nSetting: A sunlit art studio filled with easels and classic paintings\nLighting: Soft northern window light, painterly atmosphere\nStyle: Raw historical documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'medieval_queen',
    name_en: 'Medieval Queen',
    name_he: 'מלכה ימי-ביניימית',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/girls/medieval-queen.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Medieval Queen\nTransform into: Regal queen(s) in rich velvet robes\nBody: Wearing a golden crown and jewelry, seated on a throne or standing on a balcony\nSetting: Stone castle interior with tapestries\nLighting: Warm torchlight or natural window light\nStyle: Raw historical documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'medieval_king',
    name_en: 'Medieval King',
    name_he: 'מלך ימי-ביניימי',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/medieval-king.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Medieval King\nTransform into: Powerful king(s) in royal armor and fur cloak\nBody: Wearing a heavy gold crown, holding a sword or scepter\nSetting: Castle throne room or battlements\nLighting: Dramatic royal lighting\nStyle: Raw historical documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'safari_guide',
    name_en: 'Safari Guide',
    name_he: 'מדריך ספארי',
    category: 'adventure',
    gender: 'all',
    image_url: '/demos/safari.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Safari Guide\nTransform into: Professional wildlife guide(s) in khaki uniform\nBody: Wearing a wide-brimmed hat, holding binoculars, standing next to a jeep\nSetting: African savanna with acacia trees and wildlife in background\nLighting: Golden hour sunlight\nStyle: Raw nature documentary photography, National Geographic style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'formula_1_driver',
    name_en: 'Racing Driver',
    name_he: 'נהג מרוצים',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/racer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Racing Driver\nTransform into: Professional racing driver(s) in fireproof racing suit\nBody: Holding a helmet under the arm, standing on the track, face fully visible\nSetting: Grand Prix race track with pit lane background\nLighting: Bright daylight, high contrast\nStyle: Raw sports documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'street_magician',
    name_en: 'Street Magician',
    name_he: 'קוסם רחוב',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/magician.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Street Magician\nTransform into: Charismatic magician(s) performing tricks\nBody: Holding playing cards or magical props, stylish modern clothing\nSetting: Busy urban street corner with onlookers\nLighting: Natural street lighting\nStyle: Raw urban documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'poker_player',
    name_en: 'Poker Player',
    name_he: 'שחקן פוקר',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/poker_player.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Poker Pro\nTransform into: High-stakes poker player(s)\nBody: Seated at a poker table with chips and cards, wearing sunglasses and hoodie or suit\nSetting: Luxurious casino room\nLighting: Dim, moody casino lighting\nStyle: Raw lifestyle documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'master_chocolatier',
    name_en: 'Master Chocolatier',
    name_he: 'שוקולטייר',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/chocolatier.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Master Chocolatier\nTransform into: Expert chocolate maker(s) in apron\nBody: Working with molten chocolate or truffles, professional stance\nSetting: Artisan chocolate kitchen\nLighting: Warm, appetizing lighting\nStyle: Raw culinary documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'gardener',
    name_en: 'Gardener',
    name_he: 'גנן',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/gardener.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Expert Gardener\nTransform into: Professional landscape gardener(s)\nBody: Wearing practical work clothes with gardening tools\nSetting: Lush botanical garden or greenhouse\nLighting: Bright natural sunlight\nStyle: Raw nature documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'construction_worker',
    name_en: 'Construction Worker',
    name_he: 'פועל בניין',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/construction_worker.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Construction Worker\nTransform into: Skilled builder(s) in high-vis vest and gear\nBody: Holding blueprints or tools, standing on site, NO HELMET blocking face\nSetting: Active construction site with steel beams\nLighting: Bright daylight, industrial atmosphere\nStyle: Raw industrial documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'miner',
    name_en: 'Miner',
    name_he: 'כורה',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/dwarf.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Miner\nTransform into: Tough miner(s) with headlamp (on forehead)\nBody: Wearing dusty work clothes, face fully visible (smudged with coal dust)\nSetting: Underground mine tunnel with support beams\nLighting: Dramatic tunnel lighting, chiaroscuro\nStyle: Raw industrial documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'ski_instructor',
    name_en: 'Ski Instructor',
    name_he: 'מדריך סקי',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/skater.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Ski Instructor\nTransform into: Professional ski instructor(s) in winter gear\nBody: Standing with skis on snowy slope, goggles on forehead (eyes visible)\nSetting: Snowy mountain peak with blue sky\nLighting: Bright snow reflection lighting\nStyle: Raw sports documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'martial_arts_master',
    name_en: 'Martial Arts Master',
    name_he: 'מאסטר אמנויות לחימה',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/samurai.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Martial Arts Master\nTransform into: Disciplined martial artist(s) in traditional gi\nBody: Dynamic fighting stance or meditation pose\nSetting: Traditional dojo with tatami mats\nLighting: Soft natural light from shoji screens\nStyle: Raw sports documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'cyborg_assassin',
    name_en: 'Cyborg Assassin',
    name_he: 'מתנקש סייבורג',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/cyborg.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Cyborg Assassin\nTransform into: Deadly cyborg(s) with metallic skin patches\nBody: Sleek futuristic combat suit, glowing red eye option, face visible\nSetting: High-tech futuristic corridor\nLighting: Cold sterile sci-fi lighting\nStyle: Raw sci-fi documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'high_elf_archer',
    name_en: 'High Elf Archer',
    name_he: 'קשת אלפים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/archer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: High Elf Archer\nTransform into: Noble elven archer(s) with fine bow\nBody: Elegant armor with leaf motifs, drawing a bow\nSetting: Golden autumn forest\nLighting: Magical golden hour light\nStyle: Raw fantasy documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'dwarf_warrior',
    name_en: 'Dwarf Warrior',
    name_he: 'לוחם גמדים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/dwarf.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Dwarf Warrior\nTransform into: Stout dwarf warrior(s) with braided beard/hair\nBody: Heavy plate armor, holding a battle axe\nSetting: Stone cavern or mountain pass\nLighting: Dramatic torchlight\nStyle: Raw fantasy documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'orc_chieftain',
    name_en: 'Orc Chieftain',
    name_he: 'מנהיג אורקים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/barbarian.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Orc Chieftain\nTransform into: Powerful orc warrior(s) (humanoid face features retained)\nBody: Tribal armor, war paint, muscular build\nSetting: Tribal camp with bonfires\nLighting: Firelight, savage atmosphere\nStyle: Raw fantasy documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'greek_god',
    name_en: 'Greek God',
    name_he: 'אל יווני',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/greek-deity.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Greek God/Goddess\nTransform into: Divine being(s) from Mount Olympus\nBody: Wearing white toga, golden laurel wreath, glowing aura\nSetting: Mount Olympus above the clouds\nLighting: Divine bright sunlight\nStyle: Raw fantasy documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'gladiator',
    name_en: 'Roman Gladiator',
    name_he: 'גלדיאטור',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/gladiator.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Roman Gladiator\nTransform into: Fierce gladiator(s) in arena armor\nBody: Battle-worn armor, holding shield and sword, no helmet\nSetting: Colosseum arena with cheering crowd\nLighting: Harsh Mediterranean sun\nStyle: Raw historical documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'detective_modern',
    name_en: 'Modern Detective',
    name_he: 'בלש מודרני',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/detective.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Modern Detective\nTransform into: Sharp police detective(s)\nBody: Wearing suit or leather jacket, showing badge\nSetting: Crime scene investigation with yellow tape\nLighting: Flashing police lights, dramatic\nStyle: Raw crime documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'pop_star',
    name_en: 'Pop Star',
    name_he: 'כוכב פופ',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/singer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Pop Icon\nTransform into: Chart-topping pop singer(s)\nBody: Stylish colorful outfit, holding microphone\nSetting: Music video set with bright colors\nLighting: High-key studio lighting\nStyle: Raw celebrity documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'movie_director',
    name_en: 'Movie Director',
    name_he: 'במאי סרטים',
    category: 'arts',
    gender: 'all',
    image_url: '/demos/commander.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Film Director\nTransform into: Visionary movie director(s)\nBody: Wearing casual clothes, holding a megaphone or viewfinder\nSetting: Movie set with cameras and crew\nLighting: Behind-the-scenes lighting\nStyle: Raw cinema documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'sculptor',
    name_en: 'Sculptor',
    name_he: 'פסל',
    category: 'arts',
    gender: 'all',
    image_url: '/demos/artist.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Sculptor\nTransform into: Artist sculptor(s) working with clay or stone\nBody: Covered in dust/clay, holding chisels\nSetting: Art studio filled with statues\nLighting: Dramatic studio lighting\nStyle: Raw art documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'doctor_male',
    name_en: 'Doctor',
    name_he: 'רופא',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/doctor.jpg',
    prompt: `${BASE_PROMPT}
Character: Elite Spy
Transform into: Elite Spy character
Body: Professional pose
Setting: Appropriate setting for Elite Spy
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'doctor_female',
    name_en: 'Doctor',
    name_he: 'רופאה',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/girls/doctor_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Cosmic Hero
Transform into: Cosmic Hero character
Body: Professional pose
Setting: Appropriate setting for Cosmic Hero
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'surgeon_male',
    name_en: 'Surgeon',
    name_he: 'מנתח',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/doctor.jpg',
    prompt: `${BASE_PROMPT}
Character: Male Surgeon
Transform into: Male Surgeon character
Body: Professional pose
Setting: Appropriate setting for Male Surgeon
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'surgeon_female',
    name_en: 'Surgeon',
    name_he: 'מנתחת',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/doctor_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Surgeon
Transform into: Female Surgeon character
Body: Professional pose
Setting: Appropriate setting for Female Surgeon
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'nurse_male',
    name_en: 'Nurse',
    name_he: 'אח',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/doctor.jpg',
    prompt: `${BASE_PROMPT}
Character: Male Nurse
Transform into: Male Nurse character
Body: Professional pose
Setting: Appropriate setting for Male Nurse
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'nurse_female',
    name_en: 'Nurse',
    name_he: 'אחות',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/doctor_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Nurse
Transform into: Female Nurse character
Body: Professional pose
Setting: Appropriate setting for Female Nurse
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'chef_male',
    name_en: 'Chef',
    name_he: 'שף',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/chef.jpg',
    prompt: `${BASE_PROMPT}
Character: Male Chef
Transform into: Male Chef character
Body: Professional pose
Setting: Appropriate setting for Male Chef
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'chef_female',
    name_en: 'Chef',
    name_he: 'שפית',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/girls/chef_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Chef
Transform into: Female Chef character
Body: Professional pose
Setting: Appropriate setting for Female Chef
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'pastry_chef_male',
    name_en: 'Pastry Chef',
    name_he: 'קונדיטור',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/sushi-chef.jpg',
    prompt: `${BASE_PROMPT}
Character: Male Pastry Chef
Transform into: Male Pastry Chef character
Body: Professional pose
Setting: Appropriate setting for Male Pastry Chef
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'pastry_chef_female',
    name_en: 'Pastry Chef',
    name_he: 'קונדיטורית',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/sushi-chef.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Pastry Chef
Transform into: Female Pastry Chef character
Body: Professional pose
Setting: Appropriate setting for Female Pastry Chef
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'pilot_male',
    name_en: 'Pilot',
    name_he: 'טייס',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/pilot.jpg',
    prompt: `${BASE_PROMPT}
Character: Male Pilot
Transform into: Male Pilot character
Body: Professional pose
Setting: Appropriate setting for Male Pilot
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'pilot_female',
    name_en: 'Pilot',
    name_he: 'טייסת',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/girls/pilot_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Pilot
Transform into: Female Pilot character
Body: Professional pose
Setting: Appropriate setting for Female Pilot
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'fighter_pilot_female',
    name_en: 'Fighter Pilot',
    name_he: 'טייסת קרב',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/fighter_pilot_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Fighter Pilot
Transform into: Female Fighter Pilot character
Body: Professional pose
Setting: Appropriate setting for Female Fighter Pilot
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'astronaut_female',
    name_en: 'Astronaut',
    name_he: 'אסטרונאוטית',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/girls/astronaut_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Astronaut
Transform into: Female Astronaut character
Body: Professional pose
Setting: Appropriate setting for Female Astronaut
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'firefighter_female',
    name_en: 'Firefighter',
    name_he: 'כבאית',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/girls/firefighter_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Firefighter
Transform into: Female Firefighter character
Body: Professional pose
Setting: Appropriate setting for Female Firefighter
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'paramedic_male',
    name_en: 'Paramedic',
    name_he: 'פרמדיק',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/magician.jpg',
    prompt: `${BASE_PROMPT}
Character: Male Paramedic
Transform into: Male Paramedic character
Body: Professional pose
Setting: Appropriate setting for Male Paramedic
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'paramedic_female',
    name_en: 'Paramedic',
    name_he: 'פרמדיקית',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/magician.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Paramedic
Transform into: Female Paramedic character
Body: Professional pose
Setting: Appropriate setting for Female Paramedic
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'police_officer_male',
    name_en: 'Police Officer',
    name_he: 'שוטר',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/police.jpg',
    prompt: `${BASE_PROMPT}
Character: Male Police Officer
Transform into: Male Police Officer character
Body: Professional pose
Setting: Appropriate setting for Male Police Officer
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'police_officer_female',
    name_en: 'Police Officer',
    name_he: 'שוטרת',
    category: 'professions',
    gender: 'female',
    image_url: '/demos/girls/police_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Police Officer
Transform into: Female Police Officer character
Body: Professional pose
Setting: Appropriate setting for Female Police Officer
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'navy_seal',
    name_en: 'Navy Seal',
    name_he: 'לוחם קומנדו ימי',
    category: 'military',
    gender: 'all',
    image_url: '/demos/navy_seal.jpg',
    prompt: `${BASE_PROMPT}
Character: Navy Seal
Transform into: Navy Seal character
Body: Professional pose
Setting: Appropriate setting for Navy Seal
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'marine',
    name_en: 'Marine',
    name_he: 'נחחת (מארינס)',
    category: 'military',
    gender: 'all',
    image_url: '/demos/marine.jpg',
    prompt: `${BASE_PROMPT}
Character: US Marine
Transform into: US Marine character
Body: Professional pose
Setting: Appropriate setting for US Marine
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'soccer_female',
    name_en: 'Soccer Player',
    name_he: 'שחקנית כדורגל',
    category: 'sports',
    gender: 'female',
    image_url: '/demos/girls/soccer_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Soccer Player
Transform into: Female Soccer Player character
Body: Professional pose
Setting: Appropriate setting for Female Soccer Player
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'basketball_female',
    name_en: 'Basketball Player',
    name_he: 'שחקנית כדורסל',
    category: 'sports',
    gender: 'female',
    image_url: '/demos/girls/basketball_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Basketball Player
Transform into: Female Basketball Player character
Body: Professional pose
Setting: Appropriate setting for Female Basketball Player
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'boxer_female',
    name_en: 'Boxer',
    name_he: 'מתאגרפת',
    category: 'sports',
    gender: 'female',
    image_url: '/demos/girls/boxer_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Boxer
Transform into: Female Boxer character
Body: Professional pose
Setting: Appropriate setting for Female Boxer
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'tennis_player',
    name_en: 'Tennis Player',
    name_he: 'טניסאי/ת',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/tennis.jpg',
    prompt: `${BASE_PROMPT}
Character: Tennis Player
Transform into: Tennis Player character
Body: Professional pose
Setting: Appropriate setting for Tennis Player
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'football_player',
    name_en: 'Football Player',
    name_he: 'שחקן פוטבול',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/football.jpg',
    prompt: `${BASE_PROMPT}
Character: Football Player
Transform into: Football Player character
Body: Professional pose
Setting: Appropriate setting for Football Player
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'baseball_player',
    name_en: 'Baseball Player',
    name_he: 'שחקן בייסבול',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/baseball.jpg',
    prompt: `${BASE_PROMPT}
Character: Baseball Player
Transform into: Baseball Player character
Body: Professional pose
Setting: Appropriate setting for Baseball Player
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'witch',
    name_en: 'Witch',
    name_he: 'מכשפה',
    category: 'fantasy',
    gender: 'female',
    image_url: '/demos/girls/witch.jpg',
    prompt: `${BASE_PROMPT}
Character: Witch
Transform into: Witch character
Body: Professional pose
Setting: Appropriate setting for Witch
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'sorcerer',
    name_en: 'Sorcerer',
    name_he: 'מכשף',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/wizard.jpg',
    prompt: `${BASE_PROMPT}
Character: Sorcerer
Transform into: Sorcerer character
Body: Professional pose
Setting: Appropriate setting for Sorcerer
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'elf_female',
    name_en: 'Elf',
    name_he: 'אלפית',
    category: 'fantasy',
    gender: 'female',
    image_url: '/demos/girls/elf_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Elf
Transform into: Female Elf character
Body: Professional pose
Setting: Appropriate setting for Female Elf
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'elf_male',
    name_en: 'Elf',
    name_he: 'אלף',
    category: 'fantasy',
    gender: 'male',
    image_url: '/demos/elf.jpg',
    prompt: `${BASE_PROMPT}
Character: Male Elf
Transform into: Male Elf character
Body: Professional pose
Setting: Appropriate setting for Male Elf
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'knight_female',
    name_en: 'Knight',
    name_he: 'אבירה',
    category: 'historical',
    gender: 'female',
    image_url: '/demos/girls/knight_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Knight
Transform into: Female Knight character
Body: Professional pose
Setting: Appropriate setting for Female Knight
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'samurai_female',
    name_en: 'Samurai',
    name_he: 'סמוראית',
    category: 'historical',
    gender: 'female',
    image_url: '/demos/girls/samurai_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Samurai
Transform into: Female Samurai character
Body: Professional pose
Setting: Appropriate setting for Female Samurai
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'viking_female',
    name_en: 'Viking',
    name_he: 'ויקינגית',
    category: 'historical',
    gender: 'female',
    image_url: '/demos/girls/viking-woman.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Viking
Transform into: Female Viking character
Body: Professional pose
Setting: Appropriate setting for Female Viking
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'spartan',
    name_en: 'Spartan Warrior',
    name_he: 'לוחם ספרטני',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/knight.jpg',
    prompt: `${BASE_PROMPT}
Character: Spartan Warrior
Transform into: Spartan Warrior character
Body: Professional pose
Setting: Appropriate setting for Spartan Warrior
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'roman_soldier',
    name_en: 'Roman Soldier',
    name_he: 'חייל רומאי',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/roman.jpg',
    prompt: `${BASE_PROMPT}
Character: Roman Soldier
Transform into: Roman Soldier character
Body: Professional pose
Setting: Appropriate setting for Roman Soldier
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'pirate_female',
    name_en: 'Pirate',
    name_he: 'פיראטית',
    category: 'historical',
    gender: 'female',
    image_url: '/demos/girls/pirate_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Pirate
Transform into: Female Pirate character
Body: Professional pose
Setting: Appropriate setting for Female Pirate
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'cleopatra',
    name_en: 'Cleopatra',
    name_he: 'קליאופטרה',
    category: 'historical',
    gender: 'female',
    image_url: '/demos/cleopatra.jpg',
    prompt: `${BASE_PROMPT}
Character: Egyptian Queen Cleopatra
Transform into: Egyptian Queen Cleopatra character
Body: Professional pose
Setting: Appropriate setting for Egyptian Queen Cleopatra
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'cowgirl',
    name_en: 'Cowgirl',
    name_he: 'בוקרת',
    category: 'historical',
    gender: 'female',
    image_url: '/demos/girls/cowgirl.jpg',
    prompt: `${BASE_PROMPT}
Character: Western Cowgirl
Transform into: Western Cowgirl character
Body: Professional pose
Setting: Appropriate setting for Western Cowgirl
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'native_american_chief',
    name_en: 'Native American Chief',
    name_he: 'צ\'יף אינדיאני',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/native_american_chief.jpg',
    prompt: `${BASE_PROMPT}
Character: Native American Chief
Transform into: Native American Chief character
Body: Professional pose
Setting: Appropriate setting for Native American Chief
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'geisha',
    name_en: 'Geisha',
    name_he: 'גיישה',
    category: 'historical',
    gender: 'female',
    image_url: '/demos/geisha.jpg',
    prompt: `${BASE_PROMPT}
Character: Traditional Geisha
Transform into: Traditional Geisha character
Body: Professional pose
Setting: Appropriate setting for Traditional Geisha
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'ceo_female',
    name_en: 'CEO',
    name_he: 'מנכ"לית',
    category: 'business',
    gender: 'female',
    image_url: '/demos/girls/ceo_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female CEO
Transform into: Female CEO character
Body: Professional pose
Setting: Appropriate setting for Female CEO
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'lawyer',
    name_en: 'Lawyer',
    name_he: 'עורך דין',
    category: 'business',
    gender: 'all',
    image_url: '/demos/lawyer.jpg',
    prompt: `${BASE_PROMPT}
Character: Professional Lawyer
Transform into: Professional Lawyer character
Body: Professional pose
Setting: Appropriate setting for Professional Lawyer
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'scientist',
    name_en: 'Scientist',
    name_he: 'מדען',
    category: 'business',
    gender: 'all',
    image_url: '/demos/scientist.jpg',
    prompt: `${BASE_PROMPT}
Character: Research Scientist
Transform into: Research Scientist character
Body: Professional pose
Setting: Appropriate setting for Research Scientist
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'teacher',
    name_en: 'Teacher',
    name_he: 'מורה',
    category: 'business',
    gender: 'all',
    image_url: '/demos/teacher.jpg',
    prompt: `${BASE_PROMPT}
Character: School Teacher
Transform into: School Teacher character
Body: Professional pose
Setting: Appropriate setting for School Teacher
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'architect',
    name_en: 'Architect',
    name_he: 'אדריכל',
    category: 'business',
    gender: 'all',
    image_url: '/demos/architect.jpg',
    prompt: `${BASE_PROMPT}
Character: Professional Architect
Transform into: Professional Architect character
Body: Professional pose
Setting: Appropriate setting for Professional Architect
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'rockstar_female',
    name_en: 'Rockstar',
    name_he: 'רוקסטאר',
    category: 'entertainment',
    gender: 'female',
    image_url: '/demos/girls/rockstar_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Female Rock Star
Transform into: Female Rock Star character
Body: Professional pose
Setting: Appropriate setting for Female Rock Star
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'popstar',
    name_en: 'Pop Star',
    name_he: 'כוכב פופ',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/singer.jpg',
    prompt: `${BASE_PROMPT}
Character: Pop Star
Transform into: Pop Star character
Body: Professional pose
Setting: Appropriate setting for Pop Star
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'dancer',
    name_en: 'Dancer',
    name_he: 'רקדן',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/dancer.jpg',
    prompt: `${BASE_PROMPT}
Character: Professional Dancer
Transform into: Professional Dancer character
Body: Professional pose
Setting: Appropriate setting for Professional Dancer
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'painter',
    name_en: 'Painter',
    name_he: 'צייר',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/artist.jpg',
    prompt: `${BASE_PROMPT}
Character: Painter
Transform into: Painter character
Body: Professional pose
Setting: Appropriate setting for Painter
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'photographer',
    name_en: 'Photographer',
    name_he: 'צלם',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/photographer-.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Photographer(s)\nTransform into: Professional photographer(s) with camera equipment\nBody: Artistic photography pose with professional cameras\nSetting: Photo studio or outdoor photography location\nLighting: Professional photography lighting setup\nStyle: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'mountaineer',
    name_en: 'Mountaineer',
    name_he: 'מטפס הרים',
    category: 'adventure',
    gender: 'all',
    image_url: '/demos/mountaineer.jpg',
    prompt: `${BASE_PROMPT}
Character: Mountain Climber
Transform into: Mountain Climber character
Body: Professional pose
Setting: Appropriate setting for Mountain Climber
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'cyberpunk',
    name_en: 'Cyberpunk',
    name_he: 'סייברפאנק',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/cyberpunk-hacker.jpg',
    prompt: `${BASE_PROMPT}
Character: Cyberpunk
Transform into: Cyberpunk character
Body: Professional pose
Setting: Appropriate setting for Cyberpunk
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'space_marine',
    name_en: 'Space Marine',
    name_he: 'נחחת חלל',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/space-marine.jpg',
    prompt: `${BASE_PROMPT}
Character: Sci-Fi Space Marine
Transform into: Sci-Fi Space Marine character
Body: Professional pose
Setting: Appropriate setting for Sci-Fi Space Marine
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'time_traveler',
    name_en: 'Time Traveler',
    name_he: 'נוסע בזמן',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/time-traveler.jpg',
    prompt: `${BASE_PROMPT}
Character: Time Traveler
Transform into: Time Traveler character
Body: Professional pose
Setting: Appropriate setting for Time Traveler
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'vampire',
    name_en: 'Vampire',
    name_he: 'ערפד',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/vampire.jpg',
    prompt: `${BASE_PROMPT}
Character: Vampire
Transform into: Vampire character
Body: Professional pose
Setting: Appropriate setting for Vampire
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'werewolf_human',
    name_en: 'Werewolf',
    name_he: 'אדם זאב',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/werewolf.jpg',
    prompt: `${BASE_PROMPT}
Character: Werewolf
Transform into: Werewolf character
Body: Professional pose
Setting: Appropriate setting for Werewolf
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'superhero_custom',
    name_en: 'Custom Superhero',
    name_he: 'גיבור על מותאם אישית',
    category: 'heroes',
    gender: 'all',
    image_url: '/demos/girls/superheroine.jpg',
    prompt: `${BASE_PROMPT}
Character: Superhero
Transform into: Superhero character
Body: Professional pose
Setting: Appropriate setting for Superhero
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'monk',
    name_en: 'Monk',
    name_he: 'נזיר',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/monk.jpg',
    prompt: `${BASE_PROMPT}
Character: Monk
Transform into: Monk character
Body: Professional pose
Setting: Appropriate setting for Monk
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'business_professional',
    name_en: 'Business Professional',
    name_he: 'איש עסקים',
    category: 'business',
    gender: 'all',
    image_url: '/demos/businessman.jpg',
    prompt: `${BASE_PROMPT}
Character: Business Professional
Transform into: Business Professional character
Body: Professional pose
Setting: Appropriate setting for Business Professional
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'fashion_model',
    name_en: 'Fashion Model',
    name_he: 'דוגמן/ית',
    category: 'entertainment',
    gender: 'all',
    image_url: '/demos/fashion_model.jpg',
    prompt: `${BASE_PROMPT}
Character: Fashion Model
Transform into: Fashion Model character
Body: Professional pose
Setting: Appropriate setting for Fashion Model
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'athlete_general',
    name_en: 'Athlete',
    name_he: 'אתלט/ית',
    category: 'sports',
    gender: 'all',
    image_url: '/demos/girls/athlete_female.jpg',
    prompt: `${BASE_PROMPT}
Character: Athlete
Transform into: Athlete character
Body: Professional pose
Setting: Appropriate setting for Athlete
Lighting: Professional lighting
Style: Raw documentary photography, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'marjorie',
    name_en: 'Marjorie',
    name_he: 'מרגרת',
    category: 'entertainment',
    gender: 'female',
    image_url: '/demos/marjorie.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Marjorie\nTransform into: A sophisticated and elegant woman with a glamorous appearance\nBody: Wearing a luxurious evening gown with intricate beadwork, holding a crystal glass\nSetting: A luxurious ballroom with chandeliers and ornate decorations\nLighting: Warm, soft lighting with golden highlights\nStyle: Glamorous Hollywood-style portrait`
  },
  {
    id: 'cosmic_heroine',
    name_en: 'Cosmic Heroine',
    name_he: 'גיבורה קוסמית',
    category: 'fantasy',
    gender: 'female',
    image_url: '/demos/cosmic_heroine.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Cosmic Heroine\nTransform into: A powerful space warrior with cosmic powers\nBody: Wearing a sleek, futuristic armor with glowing energy patterns, holding a staff of light\nSetting: A star-filled galaxy with distant planets and nebulae\nLighting: Cosmic glow with vibrant colors from stars and nebulae\nStyle: Epic sci-fi fantasy, cinematic VFX style`
  },
  {
    id: 'police_officer',
    name_en: 'Police Officer',
    name_he: 'שוטר',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/police_officer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Police Officer\nTransform into: A professional law enforcement officer in uniform\nBody: Standing confidently in police uniform with badge and equipment\nSetting: Urban street or police station interior\nLighting: Bright, professional lighting\nStyle: Realistic law enforcement photography`
  },
  {
    id: 'pramedic',
    name_en: 'Pramedic',
    name_he: 'פרמדיק',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/pramedic.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Pramedic\nTransform into: A skilled medical professional in modern healthcare setting\nBody: Wearing a white medical coat with a stethoscope, examining patient with medical device\nSetting: Hospital or clinic examination room\nLighting: Bright, clinical lighting\nStyle: Professional medical photography, healthcare aesthetic`
  },
  {
    id: 'confectioner',
    name_en: 'Confectioner',
    name_he: 'קונדיטור',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/confectioner.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Confectioner\nTransform into: A skilled pastry chef in professional kitchen attire\nBody: Wearing a white chef's hat and apron, holding a pastry brush\nSetting: Modern bakery or pastry kitchen\nLighting: Bright, professional kitchen lighting\nStyle: Professional baking photography, culinary aesthetic`
  },
  {
    id: 'modern_bartender',
    name_en: 'Modern Bartender',
    name_he: 'ברטנדר מודרני',
    category: 'professions',
    gender: 'male',
    image_url: '/demos/modern_bartender.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Modern Bartender\nTransform into: A skilled mixologist in a trendy bar setting\nBody: Wearing a stylish bartender shirt, mixing cocktails with a shaker\nSetting: Upscale modern bar with neon lighting\nLighting: Dramatic bar lighting with neon accents\nStyle: Contemporary bar photography, nightlife aesthetic`
  },
  {
    id: 'gibor_kosmos',
    name_en: 'Cosmic Hero',
    name_he: 'גיבור קוסמוס',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/gibor_kosmos.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Cosmic Hero\nTransform into: Powerful cosmic superhero with glowing powers\nBody: Floating in space with cosmic energy surrounding, wearing futuristic suit with glowing elements\nSetting: Deep space with stars and nebulae\nLighting: Dramatic cosmic lighting with glowing effects\nStyle: Epic superhero photography, cinematic style, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'ballet_dancer',
    name_en: 'Ballet Dancer',
    name_he: 'רקדן/ית בלט',
    category: 'arts',
    gender: 'all',
    image_url: '/demos/ballet_dancer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Ballet Dancer\nTransform into: Professional ballet dancer\nBody: Elegant ballet pose, wearing classical ballet costume and pointe shoes\nSetting: Grand dance studio with mirrors and barre\nLighting: Soft theatrical lighting from stage lights\nStyle: Classical dance photography, elegant and graceful, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'indian_chef',
    name_en: 'Indian Chef',
    name_he: 'טבח הודי',
    category: 'professions',
    gender: 'all',
    image_url: '/demos/indian_chef.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Indian Chef\nTransform into: Traditional Indian chef\nBody: Wearing turban and traditional Indian chef attire, standing in professional kitchen\nSetting: Authentic Indian restaurant kitchen with spices and cooking utensils\nLighting: Warm cultural lighting with Indian restaurant ambiance\nStyle: Cultural culinary photography, authentic Indian setting, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'angel_guardian',
    name_en: 'Guardian Angel',
    name_he: 'מלאך שומר',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/angel_guardian.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Guardian Angel\nTransform into: Majestic guardian angel\nBody: Large white wings, wearing flowing white robes, protective stance\nSetting: Between heaven and earth, ethereal atmosphere\nLighting: Divine heavenly glow with dramatic contrast\nStyle: Spiritual fantasy photography, ethereal and majestic, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'malach_yamim',
    name_en: 'Angel of Days',
    name_he: 'מלאך ימים',
    category: 'fantasy',
    gender: 'all',
    image_url: '/demos/malach_yamim.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Angel of Days\nTransform into: Mysterious angel of time\nBody: Wearing ancient flowing robes, holding celestial hourglass or time artifact\nSetting: Standing between light and shadow, mystical atmosphere\nLighting: Dramatic mystical lighting with temporal effects\nStyle: Mythical fantasy photography, mysterious and ancient, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'roman_centurion',
    name_en: 'Roman Centurion',
    name_he: 'קנטוריון רומי',
    category: 'historical',
    gender: 'all',
    image_url: '/demos/roman_centurion.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Roman Centurion\nTransform into: Ancient Roman military commander\nBody: Wearing full centurion armor with helmet, holding gladius sword and shield\nSetting: Ancient Roman forum or battlefield with classical architecture\nLighting: Dramatic historical lighting with Mediterranean sun\nStyle: Historical documentary photography, ancient Rome aesthetic, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'robot_engineer',
    name_en: 'Robot Engineer',
    name_he: 'מהנדס רובוטיקה',
    category: 'scifi',
    gender: 'all',
    image_url: '/demos/robot_engineer.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Robot Engineer\nTransform into: Advanced robotics engineer\nBody: Wearing lab coat over futuristic clothing, surrounded by mechanical parts and tools\nSetting: High-tech robotics workshop with advanced equipment\nLighting: Professional technical lighting with blue and white accents\nStyle: Technical sci-fi photography, futuristic laboratory, 8k photograph, hyper-realistic, f/1.8 aperture`
  },
  {
    id: 'caricature',
    name_en: 'Caricature Art',
    name_he: 'קריקטורה',
    category: 'arts',
    gender: 'all',
    image_url: '/demos/caricature.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Caricature Art\nTransform into: A highly detailed 3D caricature art style\nBody: Exaggerated stylized body with funny casual outfit, slightly larger head proportion\nSetting: Colorful abstract studio background\nLighting: Bright, vibrant studio lighting\nStyle: 3D Pixar caricature style, digital art, stylized body BUT EXACTLY PHOTOREALISTIC HUMAN FACE, hyper-realistic face matching reference perfectly, do not distort facial features`
  },
  {
    id: 'comics',
    name_en: 'Comic Book',
    name_he: 'קומיקס',
    category: 'arts',
    gender: 'all',
    image_url: '/demos/comics.jpg',
    prompt: `${BASE_PROMPT}\nCharacter: Comic Book Character\nTransform into: A dynamic comic book illustration style\nBody: Wearing stylized action clothing, dynamic comic pose\nSetting: Graphic novel panel background with halftone dots and comic effects\nLighting: High-contrast ink shadows, vibrant comic colors\nStyle: Graphic novel illustration, comic book art, cell shading, BUT keep the face photorealistic and matching the reference perfectly`
  }
];

export const CHARACTERS = RAW_CHARACTERS.map(applyExpertPrompt);

export const PRICING_PLANS = [
  {
    id: 'starter',
    name_en: 'Starter',
    name_he: 'התחלה',
    credits: 25,
    bonus: 10,
    totalCredits: 35,
    price: 4.90,
    pricePerCredit: 0.140,
    popular: false,
    description_en: 'Perfect for trying out',
    description_he: 'מושלם לניסיון'
  },
  {
    id: 'popular',
    name_en: 'Popular',
    name_he: 'פופולרי',
    credits: 60,
    bonus: 25,
    totalCredits: 85,
    price: 9.90,
    pricePerCredit: 0.116,
    popular: true,
    savings: 17,
    description_en: 'Best value for most users',
    description_he: 'הערך הכי טוב לרוב המשתמשים'
  },
  {
    id: 'pro',
    name_en: 'Professional',
    name_he: 'מקצועי',
    credits: 120,
    bonus: 60,
    totalCredits: 180,
    price: 19.90,
    pricePerCredit: 0.111,
    popular: false,
    savings: 21,
    description_en: 'For content creators',
    description_he: 'ליוצרי תוכן'
  },
  {
    id: 'business',
    name_en: 'Business',
    name_he: 'עסקי',
    credits: 250,
    bonus: 150,
    totalCredits: 400,
    price: 39.90,
    pricePerCredit: 0.100,
    popular: false,
    savings: 29,
    description_en: 'For teams & events',
    description_he: 'לצוותים ואירועים'
  },
  {
    id: 'enterprise',
    name_en: 'Enterprise',
    name_he: 'ארגוני',
    credits: 600,
    bonus: 400,
    totalCredits: 1000,
    price: 89.90,
    pricePerCredit: 0.090,
    popular: false,
    savings: 36,
    description_en: 'Unlimited creativity',
    description_he: 'יצירתיות ללא הגבלה'
  }
];

// ─── "Surprise Me" ─────────────────────────────────────────────────────────
// Picking this tile rolls a character at random. The pool is these concepts —
// which deliberately do NOT appear in the grid, so the guest gets something they
// could not have picked themselves — plus every regular character, so the roll
// still reaches the well-tuned prompts. Rolling happens in Photobooth.jsx.
export const SURPRISE_ID = 'surprise_me';

export const SURPRISE_CHARACTER = {
  id: SURPRISE_ID,
  name_en: 'Surprise Me',
  name_he: 'תפתיע אותי',
  category: 'all',
  gender: 'all',
  isSurprise: true,
  image_url: '/demos/comics.jpg',
  prompt: '',
};

// [id, English name, Hebrew name, scene description]
const SURPRISE_ONLY = [
  ['deep_sea_explorer', 'Deep Sea Explorer', 'חוקר מעמקים', 'Deep-sea explorer in a brass-and-steel atmospheric diving suit with the helmet open and carried under one arm, standing on a bioluminescent ocean floor, shafts of blue light from far above, drifting particles, glowing coral and jellyfish'],
  ['storm_chaser', 'Storm Chaser', 'רודף סופות', 'Storm chaser in a weathered rain jacket beside instruments on a flat prairie road, an enormous supercell thunderstorm and lightning behind, dramatic green-grey sky, wind-blown hair, documentary photography'],
  ['lighthouse_keeper', 'Lighthouse Keeper', 'שומר המגדלור', 'Lighthouse keeper in a heavy wool sweater and oilskin coat at the top of a stone lighthouse, storm waves crashing below, the warm lamp glowing behind, cold blue dusk, cinematic'],
  ['glassblower', 'Master Glassblower', 'אמן זכוכית', 'Master glassblower in a leather apron shaping molten glass on a blowpipe, glowing furnace light across the face, dark workshop, sparks and heat haze, rich orange rim lighting'],
  ['arctic_researcher', 'Arctic Researcher', 'חוקר קוטב', 'Arctic researcher in a red expedition parka with the hood down, standing on pack ice beside scientific equipment, low golden polar sun, distant icebergs, crisp cold air'],
  ['vinyl_dj', 'Vinyl DJ', 'תקליטן ויניל', 'Vinyl DJ behind turntables in a smoky underground club, hands on the records, magenta and cyan stage lighting, crowd silhouettes and haze, shallow depth of field'],
  ['falconer', 'Royal Falconer', 'מאלף בזים', 'Falconer in embroidered medieval hunting clothes with a thick leather gauntlet, a hooded falcon perched on the wrist, misty moorland at dawn, painterly natural light'],
  ['train_conductor', 'Orient Express Conductor', 'כרטיסן הרכבת', 'Railway conductor in a crisp navy 1920s uniform with brass buttons and a peaked cap, standing in the corridor of a luxury wood-panelled sleeper train, warm lamplight, steam drifting past the window'],
  ['volcanologist', 'Volcanologist', 'חוקר הרי געש', 'Volcanologist in a heat-reflective silver suit with the visor up and the face clear, standing on black basalt near glowing lava flows at night, orange light on the face, ash in the air'],
  ['shadow_puppeteer', 'Shadow Puppeteer', 'מפעיל תיאטרון צלליות', 'Puppeteer behind a lit silk screen operating ornate shadow puppets, warm lantern glow, intricate cut-paper figures casting shadows, traditional theatre, atmospheric'],
  ['beekeeper', 'Beekeeper', 'כוורן', 'Beekeeper in a white suit with the veil lifted back clear of the face, holding a honeycomb frame in a sunlit summer meadow, bees in golden backlight, soft warm haze'],
  ['cartographer', 'Renaissance Cartographer', 'קרטוגרף', 'Renaissance cartographer in a velvet doublet leaning over a huge hand-drawn world map, brass instruments, dividers and globes, candlelight in a wood-panelled study, Old Master lighting'],
  ['rally_navigator', 'Rally Navigator', 'נווט ראלי', 'Rally co-driver in a fireproof racing suit and open-face helmet holding pace notes beside a mud-spattered rally car on a forest stage, dust and low sun through the trees, motorsport photography'],
  ['perfumer', 'Master Perfumer', 'בַּשָּׂם', 'Master perfumer in an elegant lab coat at an organ of hundreds of amber glass bottles, holding a scent strip, soft window light, Parisian atelier, refined and warm'],
  ['kite_surfer', 'Kite Surfer', 'גולש עפיפון', 'Kite surfer in a wetsuit on a turquoise ocean, spray flying, a huge colourful kite against a clear sky, action photography frozen mid-turn, bright sunlight'],
  ['clockmaker', 'Clockmaker', 'שען', 'Clockmaker with a jeweller loupe pushed up onto the forehead, working on an ornate mechanical clock surrounded by gears and tools, warm workshop light, hundreds of clocks on the walls'],
  ['aurora_photographer', 'Aurora Photographer', 'צלם זוהר צפוני', 'Night photographer in a heavy winter coat beside a tripod under a vivid green and violet aurora, snow-covered landscape, star-filled sky, long-exposure look'],
  ['sommelier', 'Sommelier', 'סומלייה', 'Sommelier in a tailored black suit with a tastevin on a chain, holding a glass up to the light in an ancient stone wine cellar, rows of barrels, candle and lantern glow'],
  ['venetian_carnival', 'Venetian Carnival', 'קרנבל ונציאני', 'Venetian carnival figure in an opulent brocade costume with feathers and gold trim, an ornate mask held to one side in the hand so the face stays fully visible, canal and lantern light at dusk'],
  ['tea_master', 'Tea Master', 'מאסטר תה', 'Tea master in refined traditional robes performing a tea ceremony in a tatami room, steam rising from the bowl, shoji screens, soft diffused daylight, serene composition'],
  ['blacksmith', 'Blacksmith', 'נפח', 'Blacksmith in a scorched leather apron striking glowing steel on an anvil, sparks flying, dark forge lit by the orange furnace, strong stance, dramatic contrast'],
  ['cliff_diver', 'Cliff Diver', 'צוללן צוקים', 'Cliff diver standing on a limestone ledge high above brilliant turquoise water, arms raised, sunlit sea cliffs, sharp action photography, dramatic sense of height'],
  ['expedition_chef', 'Expedition Chef', 'שף משלחת', 'Expedition chef in a white jacket cooking in the galley of a polar research vessel, steam and copper pans, a porthole showing icy sea, warm interior light against the cold blue outside'],
  ['neon_noir', 'Neon Noir Detective', 'בלש ניאון', 'Detective in a long coat under a flickering neon sign on a rain-slicked night street, reflections in puddles, steam from a grate, cyan and magenta light, cinematic noir'],
];

export const SURPRISE_ONLY_CHARACTERS = SURPRISE_ONLY.map(([id, name_en, name_he, scene]) =>
  applyExpertPrompt({
    id,
    name_en,
    name_he,
    category: 'adventure',
    gender: 'all',
    isSurpriseOnly: true,
    image_url: '/demos/comics.jpg',
    prompt: `Character: ${name_en}\nTransform into: ${scene}`,
  })
);

/** Everything a "Surprise Me" roll can land on. */
export const SURPRISE_POOL = [...SURPRISE_ONLY_CHARACTERS, ...CHARACTERS];
